import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const PORTAL_USER_UPDATED_EVENT = 'portal_user_updated';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const BEACH_IMAGE = '/images/recreation-beach.jpg';

const VACATION_ICONS = [
  { id: 'wave', emoji: '🌊' },
  { id: 'sunset', emoji: '🌅' },
  { id: 'sunrise', emoji: '🌄' },
  { id: 'night', emoji: '🌃' },
  { id: 'city', emoji: '🌆' },
  { id: 'sun', emoji: '☀️' },
  { id: 'moon', emoji: '🌙' },
  { id: 'star', emoji: '⭐' },
  { id: 'sparkles', emoji: '✨' },
  { id: 'rainbow', emoji: '🌈' },
  { id: 'cloud', emoji: '☁️' },
  { id: 'lightning', emoji: '⚡' },
  { id: 'fire', emoji: '🔥' },
  { id: 'snow', emoji: '❄️' },
  { id: 'sail', emoji: '⛵' },
  { id: 'ship', emoji: '🚢' },
  { id: 'plane', emoji: '✈️' },
  { id: 'rocket', emoji: '🚀' },
  { id: 'car', emoji: '🚗' },
  { id: 'taxi', emoji: '🚕' },
  { id: 'train', emoji: '🚄' },
  { id: 'bike', emoji: '🚲' },
  { id: 'island', emoji: '🏝️' },
  { id: 'palm', emoji: '🌴' },
  { id: 'cactus', emoji: '🌵' },
  { id: 'mountain', emoji: '🏔️' },
  { id: 'volcano', emoji: '🌋' },
  { id: 'tent', emoji: '⛺' },
  { id: 'map', emoji: '🗺️' },
  { id: 'compass', emoji: '🧭' },
  { id: 'luggage', emoji: '🧳' },
  { id: 'ticket', emoji: '🎫' },
  { id: 'dive', emoji: '🤿' },
  { id: 'surf', emoji: '🏄' },
  { id: 'swim', emoji: '🏊' },
  { id: 'parasol', emoji: '⛱️' },
  { id: 'shades', emoji: '🕶️' },
  { id: 'hat', emoji: '🧢' },
  { id: 'crown', emoji: '👑' },
  { id: 'camera', emoji: '📷' },
  { id: 'film', emoji: '🎥' },
  { id: 'shell', emoji: '🐚' },
  { id: 'coral', emoji: '🪸' },
  { id: 'fish', emoji: '🐠' },
  { id: 'blowfish', emoji: '🐡' },
  { id: 'dolphin', emoji: '🐬' },
  { id: 'whale', emoji: '🐋' },
  { id: 'octopus', emoji: '🐙' },
  { id: 'crab', emoji: '🦀' },
  { id: 'shrimp', emoji: '🦐' },
  { id: 'cocktail', emoji: '🍸' },
  { id: 'tropical', emoji: '🍹' },
  { id: 'beer', emoji: '🍺' },
  { id: 'cheers', emoji: '🍻' },
  { id: 'wine', emoji: '🍷' },
  { id: 'champagne', emoji: '🥂' },
  { id: 'whiskey', emoji: '🥃' },
  { id: 'coffee', emoji: '☕' },
  { id: 'bubbletea', emoji: '🧋' },
  { id: 'icecream', emoji: '🍦' },
  { id: 'cake', emoji: '🍰' },
  { id: 'donut', emoji: '🍩' },
  { id: 'pizza', emoji: '🍕' },
  { id: 'burger', emoji: '🍔' },
  { id: 'sushi', emoji: '🍣' },
  { id: 'ramen', emoji: '🍜' },
  { id: 'taco', emoji: '🌮' },
  { id: 'avocado', emoji: '🥑' },
  { id: 'watermelon', emoji: '🍉' },
  { id: 'pineapple', emoji: '🍍' },
  { id: 'mango', emoji: '🥭' },
  { id: 'lemon', emoji: '🍋' },
  { id: 'peach', emoji: '🍑' },
  { id: 'strawberry', emoji: '🍓' },
  { id: 'cherry', emoji: '🍒' },
  { id: 'grape', emoji: '🍇' },
  { id: 'chili', emoji: '🌶️' },
  { id: 'dog', emoji: '🐶' },
  { id: 'cat', emoji: '🐱' },
  { id: 'bear', emoji: '🐻' },
  { id: 'panda', emoji: '🐼' },
  { id: 'fox', emoji: '🦊' },
  { id: 'tiger', emoji: '🐯' },
  { id: 'lion', emoji: '🦁' },
  { id: 'frog', emoji: '🐸' },
  { id: 'penguin', emoji: '🐧' },
  { id: 'chick', emoji: '🐥' },
  { id: 'owl', emoji: '🦉' },
  { id: 'flamingo', emoji: '🦩' },
  { id: 'parrot', emoji: '🦜' },
  { id: 'turtle', emoji: '🐢' },
  { id: 'dino', emoji: '🦕' },
  { id: 'unicorn', emoji: '🦄' },
  { id: 'dragon', emoji: '🐉' },
  { id: 'bee', emoji: '🐝' },
  { id: 'butterfly', emoji: '🦋' },
  { id: 'smile', emoji: '😄' },
  { id: 'cool', emoji: '😎' },
  { id: 'love', emoji: '😍' },
  { id: 'wink', emoji: '😉' },
  { id: 'think', emoji: '🤔' },
  { id: 'party', emoji: '🥳' },
  { id: 'ghost', emoji: '👻' },
  { id: 'alien', emoji: '👽' },
  { id: 'robot', emoji: '🤖' },
  { id: 'devil', emoji: '😈' },
  { id: 'angel', emoji: '😇' },
  { id: 'heart', emoji: '❤️' },
  { id: 'soccer', emoji: '⚽' },
  { id: 'basket', emoji: '🏀' },
  { id: 'tennis', emoji: '🎾' },
  { id: 'game', emoji: '🎮' },
  { id: 'dice', emoji: '🎲' },
  { id: 'music', emoji: '🎵' },
  { id: 'guitar', emoji: '🎸' },
  { id: 'mic', emoji: '🎤' },
  { id: 'headphones', emoji: '🎧' },
  { id: 'book', emoji: '📚' },
  { id: 'paint', emoji: '🎨' },
  { id: 'trophy', emoji: '🏆' },
  { id: 'medal', emoji: '🥇' },
  { id: 'target', emoji: '🎯' },
  { id: 'bulb', emoji: '💡' },
  { id: 'gem', emoji: '💎' },
  { id: 'key', emoji: '🔑' },
  { id: 'gift', emoji: '🎁' },
  { id: 'balloon', emoji: '🎈' },
  { id: 'confetti', emoji: '🎉' },
];

const getVacationIcon = (id) => VACATION_ICONS.find((item) => item.id === id) || null;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.roles)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const savePortalUser = (user) => {
  sessionStorage.setItem(PORTAL_USER_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event(PORTAL_USER_UPDATED_EVENT));
};

const PageStyle = createGlobalStyle`
  body {
    background: #0c2230;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow:
      inset 0 -18px 22px rgba(80, 0, 0, 0.28),
      inset 0 12px 14px rgba(255, 255, 255, 0.28),
      0 16px 0 #7f1111,
      0 0 0 8px rgba(225, 29, 46, 0.18),
      0 22px 36px rgba(0, 0, 0, 0.28);
  }
  50% {
    box-shadow:
      inset 0 -18px 22px rgba(80, 0, 0, 0.28),
      inset 0 12px 14px rgba(255, 255, 255, 0.28),
      0 16px 0 #7f1111,
      0 0 0 18px rgba(225, 29, 46, 0),
      0 22px 36px rgba(0, 0, 0, 0.28);
  }
`;

const pressIn = keyframes`
  0% { transform: translateY(0); }
  40% { transform: translateY(10px); }
  100% { transform: translateY(0); }
`;

const lockFlash = keyframes`
  0% { opacity: 0.95; }
  35% { opacity: 0.35; }
  100% { opacity: 0.12; }
`;

const lockPop = keyframes`
  0% { transform: scale(0.72); opacity: 0; }
  60% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const confettiFall = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(130px) rotate(220deg); opacity: 0; }
`;

const waitPulse = keyframes`
  0%, 100% { opacity: 0.42; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1); }
`;

const wordFlashIn = keyframes`
  0% { opacity: 0; transform: translateY(8px) scale(0.96); }
  18% { opacity: 1; transform: translateY(0) scale(1); }
  78% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
`;

let buzzerAudioCtx = null;

const unlockBuzzerAudio = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!buzzerAudioCtx) buzzerAudioCtx = new AudioCtx();
  if (buzzerAudioCtx.state === 'suspended') buzzerAudioCtx.resume();
  return buzzerAudioCtx;
};

const playBuzzerSound = () => {
  const ctx = unlockBuzzerAudio();
  if (!ctx) return;
  const now = ctx.currentTime;
  const buzz = ctx.createOscillator();
  const buzzGain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  buzz.type = 'sawtooth';
  buzz.frequency.setValueAtTime(196, now);
  buzz.frequency.exponentialRampToValueAtTime(88, now + 0.48);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, now);
  buzzGain.gain.setValueAtTime(0.0001, now);
  buzzGain.gain.exponentialRampToValueAtTime(0.24, now + 0.02);
  buzzGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
  buzz.connect(filter);
  filter.connect(buzzGain);
  buzzGain.connect(ctx.destination);
  buzz.start(now);
  buzz.stop(now + 0.52);

  const edge = ctx.createOscillator();
  const edgeGain = ctx.createGain();
  edge.type = 'square';
  edge.frequency.setValueAtTime(260, now);
  edge.frequency.exponentialRampToValueAtTime(120, now + 0.36);
  edgeGain.gain.setValueAtTime(0.0001, now);
  edgeGain.gain.exponentialRampToValueAtTime(0.07, now + 0.012);
  edgeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
  edge.connect(edgeGain);
  edgeGain.connect(ctx.destination);
  edge.start(now);
  edge.stop(now + 0.4);
};

const DRAW_COLORS = ['#e11d2e', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];

const playDrawTick = () => {
  const ctx = unlockBuzzerAudio();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(760, now);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.07, now + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.05);
};

const playDrawWin = () => {
  const ctx = unlockBuzzerAudio();
  if (!ctx) return;
  const now = ctx.currentTime;
  [523, 659, 784, 1046].forEach((freq, index) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = now + index * 0.08;
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.14, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.3);
  });
};

const easeOutCubic = (t) => 1 - ((1 - t) ** 3);

const Wrapper = styled.main`
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  padding: 1.4rem 1.1rem 2.8rem;
  color: #f7fbff;
  background:
    linear-gradient(180deg, rgba(8, 28, 40, 0.46) 0%, rgba(8, 28, 40, 0.18) 38%, rgba(8, 28, 40, 0.58) 100%),
    url(${BEACH_IMAGE}) center / cover no-repeat;
  background-attachment: scroll;
`;

const Shell = styled.section`
  position: relative;
  z-index: 1;
  width: min(880px, 100%);
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.9rem;
  margin-bottom: 1.2rem;
`;

const Identity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
`;

const IdentityMeta = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.4rem;
  color: rgba(247, 251, 255, 0.86);
  text-shadow: 0 1px 10px rgba(8, 28, 40, 0.4);
  font-weight: 500;
  font-size: 0.95rem;
`;

const FaceWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`;

const FaceButton = styled.button`
  width: ${({ $small }) => ($small ? '1.85rem' : '2.7rem')};
  height: ${({ $small }) => ($small ? '1.85rem' : '2.7rem')};
  border: 0;
  border-radius: 999px;
  background: rgba(255, 252, 248, 0.9);
  color: #12343a;
  font-size: ${({ $small }) => ($small ? '1rem' : '1.28rem')};
  line-height: 1;
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
  box-shadow: 0 8px 18px rgba(8, 28, 40, 0.16);

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

const IconPopover = styled.div`
  position: absolute;
  top: calc(100% + 0.45rem);
  left: 0;
  z-index: 30;
  width: min(18.8rem, calc(100vw - 2.4rem));
  max-height: 16.5rem;
  overflow: auto;
  padding: 0.45rem;
  border-radius: 0.9rem;
  background: rgba(255, 252, 248, 0.97);
  box-shadow: 0 16px 32px rgba(8, 28, 40, 0.24);
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.22rem;
`;

const IconOnlyPick = styled.button`
  width: 2.55rem;
  height: 2.55rem;
  border: 0;
  border-radius: 0.7rem;
  background: ${({ $active }) => ($active ? 'rgba(29, 106, 115, 0.16)' : 'transparent')};
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
`;

const TeamNameButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 0.18em;

  &:disabled {
    cursor: default;
    text-decoration: none;
  }
`;

const NamePopover = styled.form`
  position: absolute;
  top: calc(100% + 0.45rem);
  left: 0;
  z-index: 30;
  width: min(16.5rem, calc(100vw - 2.4rem));
  padding: 0.7rem 0.75rem 0.8rem;
  border-radius: 0.9rem;
  background: rgba(255, 252, 248, 0.97);
  box-shadow: 0 16px 32px rgba(8, 28, 40, 0.24);
`;

const Eyebrow = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-size: 0.68rem;
  font-weight: 700;
  color: ${({ $onCard }) => ($onCard ? '#2f6f78' : 'rgba(247, 251, 255, 0.82)')};
  text-shadow: ${({ $onCard }) => ($onCard ? 'none' : '0 1px 8px rgba(8, 28, 40, 0.35)')};
`;

const Title = styled.h1`
  margin: 0.4rem 0 0;
  font-size: ${({ $compact }) => ($compact ? 'clamp(1.12rem, 2.5vw, 1.4rem)' : 'clamp(1.55rem, 3.6vw, 2.15rem)')};
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${({ $onCard }) => ($onCard ? '#12343a' : '#f7fbff')};
  text-shadow: ${({ $onCard }) => ($onCard ? 'none' : '0 2px 16px rgba(8, 28, 40, 0.45)')};
`;

const SubText = styled.p`
  margin: 0.4rem 0 0;
  color: ${({ $onCard }) => ($onCard ? '#4d6a70' : 'rgba(247, 251, 255, 0.86)')};
  text-shadow: ${({ $onCard }) => ($onCard ? 'none' : '0 1px 10px rgba(8, 28, 40, 0.4)')};
  line-height: 1.55;
  font-weight: 500;
  font-size: 0.95rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.4rem 0 0;
  flex-wrap: wrap;

  h1, h2, h3 {
    margin: 0;
  }
`;

const HelpButton = styled.button`
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? 'rgba(29, 106, 115, 0.45)' : 'rgba(29, 106, 115, 0.22)')};
  background: ${({ $on }) => ($on ? 'rgba(29, 106, 115, 0.16)' : 'rgba(29, 106, 115, 0.06)')};
  color: #1d6a73;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`;

const HelpPanel = styled.div`
  margin-top: 0.55rem;
  padding: 0.7rem 0.8rem;
  border-radius: 0.7rem;
  background: rgba(29, 106, 115, 0.06);
  border: 1px solid rgba(29, 106, 115, 0.12);
  color: #4d6a70;
  line-height: 1.55;
  font-size: 0.9rem;
  font-weight: 500;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
`;

const GhostButton = styled.button`
  border: 1px solid rgba(247, 251, 255, 0.28);
  background: rgba(8, 28, 40, 0.32);
  color: #f7fbff;
  border-radius: 0.45rem;
  padding: 0.48rem 0.85rem;
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  backdrop-filter: blur(8px);

  ${({ $onCard }) => $onCard && css`
    border-color: rgba(28, 25, 23, 0.12);
    background: #f3ebe1;
    color: #1c1917;
  `}
`;

const Card = styled.section`
  background: rgba(255, 252, 248, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 0.95rem;
  padding: 1.2rem 1.2rem 1.25rem;
  margin-top: 0.85rem;
  box-shadow: 0 18px 40px rgba(8, 28, 40, 0.18);
  backdrop-filter: blur(16px);
  color: #12343a;
`;

const LoginCard = styled(Card)`
  max-width: 420px;
  margin: 2rem auto 0;
`;

const Label = styled.label`
  display: block;
  margin: ${({ $flush }) => ($flush ? '0 0 0.35rem' : '0.85rem 0 0.35rem')};
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6b5340;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #ddd2c3;
  background: #fffdf9;
  color: #1c1917;
  border-radius: 0.5rem;
  padding: 0.78rem 0.9rem;
  font-size: 0.98rem;

  &::placeholder {
    color: #a89a8c;
  }

  &:focus {
    outline: 2px solid rgba(29, 106, 115, 0.28);
    border-color: #1d6a73;
  }
`;

const PrimaryButton = styled.button`
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  border: 0;
  border-radius: 0.5rem;
  padding: 0.78rem 1.15rem;
  margin-top: ${({ $mt }) => ($mt ? '1rem' : '0')};
  background: #1d6a73;
  color: #fffaf4;
  font-weight: 700;
  font-size: 0.92rem;
  letter-spacing: 0.01em;
  cursor: pointer;

  &:hover {
    background: #175860;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(PrimaryButton)`
  background: #7f2d2d;

  &:hover {
    background: #6b2424;
  }
`;

const Message = styled.p`
  margin: 0.75rem 0 0;
  color: ${({ $error }) => ($error ? '#9f2d2d' : '#6b5340')};
  font-weight: 600;
  font-size: 0.88rem;
  min-height: 1.2rem;
`;

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.75rem;
  margin-top: 0.85rem;
`;

const TeamCard = styled.article`
  background: ${({ $mine }) => ($mine ? 'rgba(255, 248, 239, 0.92)' : 'rgba(255, 253, 249, 0.86)')};
  border: 1px solid ${({ $mine, $editing }) => (
    $editing || $mine ? 'rgba(29, 106, 115, 0.28)' : 'rgba(230, 220, 207, 0.8)'
  )};
  border-radius: 0.7rem;
  padding: 0.95rem 1rem;
  ${({ $editing }) => $editing && css`
    grid-column: 1 / -1;
  `}
`;

const MemberEditor = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(29, 106, 115, 0.14);
`;

const TeamName = styled.h2`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #12343a;
`;

const TeamIconMark = styled.span`
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border-radius: 0.55rem;
  background: rgba(29, 106, 115, 0.08);
  font-size: 1.15rem;
  flex-shrink: 0;
`;

const RankMark = styled.span`
  margin-left: auto;
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a08968;
  font-weight: 700;
`;

const Score = styled.p`
  margin: 0.35rem 0 0;
  font-size: 2rem;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.04em;
  color: #1c1917;
`;

const Members = styled.p`
  margin: 0.35rem 0 0;
  color: #7a7168;
  font-size: 0.8rem;
  line-height: 1.45;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;
`;

const MiniButton = styled.button`
  border: 1px solid ${({ $tone }) => ($tone === 'danger' ? '#e8c8c8' : '#e2d6c6')};
  background: ${({ $tone }) => ($tone === 'danger' ? '#f8ecec' : '#f6efe6')};
  color: ${({ $tone }) => ($tone === 'danger' ? '#7f2d2d' : '#4a3d32')};
  border-radius: 0.4rem;
  padding: 0.36rem 0.62rem;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
`;

const BuzzerWrap = styled.div`
  display: grid;
  justify-items: center;
  margin-top: 0.55rem;
`;

const BuzzerButton = styled.button`
  position: relative;
  z-index: 1;
  width: min(210px, 64vw);
  height: min(210px, 64vw);
  border: 12px solid #d7dce3;
  border-radius: 999px;
  background:
    radial-gradient(circle at 32% 26%, #ff7a7a 0%, #e11d2e 42%, #9f1239 78%);
  color: #fff;
  cursor: pointer;
  box-shadow:
    inset 0 -18px 22px rgba(80, 0, 0, 0.28),
    inset 0 12px 14px rgba(255, 255, 255, 0.28),
    0 16px 0 #7f1111,
    0 22px 36px rgba(0, 0, 0, 0.28);
  animation: ${({ $pressed, $ready }) => {
    if ($pressed) return css`${pressIn} 0.22s ease`;
    if ($ready) return css`${pulseGlow} 1.15s ease-in-out infinite`;
    return 'none';
  }};
  filter: ${({ $ready, $locked }) => {
    if ($locked) return 'grayscale(0.25) brightness(0.78)';
    if ($ready) return 'none';
    return 'grayscale(0.2) brightness(0.86)';
  }};

  &:disabled {
    cursor: not-allowed;
  }
`;

const BuzzerText = styled.span`
  display: block;
  font-size: clamp(1.35rem, 5vw, 1.8rem);
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-shadow: 0 3px 0 rgba(90, 8, 16, 0.35);
`;

const BuzzerPedestal = styled.div`
  width: min(168px, 52vw);
  height: 1.15rem;
  margin-top: -0.15rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #cfd5dd, #8b939e);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.18);
`;

const BuzzerHint = styled.p`
  margin: 0.85rem 0 0;
  text-align: center;
  color: #6b6258;
  font-weight: 500;
  font-size: 0.9rem;
`;

const LockOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 1.2rem;
  background: rgba(18, 6, 10, 0.78);
`;

const LockFlash = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(225, 29, 46, 0.72), transparent 62%);
  animation: ${lockFlash} 0.7s ease-out forwards;
  pointer-events: none;
`;

const LockCard = styled.div`
  position: relative;
  z-index: 1;
  width: min(420px, 100%);
  padding: 1.6rem 1.2rem 1.3rem;
  border-radius: 1.3rem;
  background: rgba(255, 248, 246, 0.96);
  text-align: center;
  color: #3b0d12;
  animation: ${lockPop} 0.38s ease-out;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
`;

const LockStamp = styled.p`
  margin: 0;
  font-size: clamp(2.4rem, 9vw, 3.6rem);
  font-weight: 900;
  letter-spacing: 0.12em;
  color: #e11d2e;
`;

const LockFace = styled.p`
  margin: 0.35rem 0 0;
  font-size: 3.4rem;
  line-height: 1;
`;

const LockName = styled.h2`
  margin: 0.35rem 0 0;
  font-size: clamp(1.6rem, 5vw, 2.1rem);
  letter-spacing: -0.04em;
`;

const LockTeam = styled.p`
  margin: 0.25rem 0 0;
  color: #7a4450;
  font-weight: 700;
`;

const DrawOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  padding: 1.1rem;
  background:
    radial-gradient(circle at 50% 18%, rgba(255, 196, 80, 0.3), transparent 46%),
    rgba(6, 22, 32, 0.9);
`;

const DrawStage = styled.div`
  position: relative;
  z-index: 1;
  width: min(440px, 100%);
  text-align: center;
`;

const DrawEyebrow = styled.p`
  margin: 0 0 0.35rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.72rem;
  font-weight: 800;
  color: rgba(255, 246, 216, 0.82);
`;

const DrawTitle = styled.h2`
  margin: 0 0 0.85rem;
  color: #fff8ee;
  font-size: clamp(1.5rem, 5vw, 2rem);
  letter-spacing: -0.04em;
  text-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const WheelWrap = styled.div`
  position: relative;
  width: min(320px, 78vw);
  aspect-ratio: 1;
  margin: 0 auto;
`;

const WheelPointer = styled.div`
  position: absolute;
  top: -0.15rem;
  left: 50%;
  z-index: 4;
  width: 0;
  height: 0;
  border-left: 13px solid transparent;
  border-right: 13px solid transparent;
  border-top: 28px solid #fff3c4;
  transform: translateX(-50%);
  filter: drop-shadow(0 8px 10px rgba(0, 0, 0, 0.35));
`;

const WheelDisc = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  border: 10px solid #fff3c4;
  background: ${({ $bg }) => $bg};
  transform: rotate(${({ $deg }) => $deg}deg);
  box-shadow:
    0 0 0 8px rgba(255, 243, 196, 0.16),
    0 24px 48px rgba(0, 0, 0, 0.38);
`;

const WheelHub = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  width: 4.1rem;
  height: 4.1rem;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: radial-gradient(circle at 35% 30%, #fffdf7, #f4d48b 62%, #c9952a);
  color: #6b3d0c;
  font-size: 1.55rem;
  transform: translate(-50%, -50%);
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.7), 0 8px 16px rgba(0, 0, 0, 0.2);
`;

const WheelLabel = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  max-width: 4.6rem;
  color: #fffdf8;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.15;
  text-align: center;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  transform-origin: 0 0;
  transform: rotate(${({ $angle }) => $angle}deg) translate(-50%, -6.35rem);
  pointer-events: none;
`;

const DrawWinCard = styled.div`
  position: relative;
  margin-top: 1rem;
  overflow: hidden;
  padding: 1.05rem 1rem 1.1rem;
  border-radius: 1.2rem;
  background: rgba(255, 248, 236, 0.96);
  color: #12343a;
  animation: ${lockPop} 0.4s ease-out;
`;

const ConfettiBit = styled.span`
  position: absolute;
  top: 0.2rem;
  left: ${({ $left }) => $left};
  font-size: 1.05rem;
  animation: ${confettiFall} 1.15s ease-out both;
  animation-delay: ${({ $delay }) => $delay};
  pointer-events: none;
`;

const ChosungBoard = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.7rem;
  margin: 0.85rem 0 0.4rem;
`;

const ChosungTile = styled.span`
  min-width: 4.2rem;
  padding: 0.7rem 0.85rem;
  border-radius: 1rem;
  background: #12343a;
  color: #fff8ee;
  font-size: clamp(2.1rem, 8vw, 3rem);
  font-weight: 800;
  text-align: center;
  line-height: 1;
`;

const ProgressNote = styled.p`
  margin: 0.45rem 0 0;
  text-align: center;
  color: #1d6a73;
  font-size: 1.05rem;
  font-weight: 800;
`;

const KeywordHero = styled.div`
  margin-top: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 1.1rem;
  background: linear-gradient(145deg, rgba(29, 106, 115, 0.14), rgba(255, 248, 235, 0.9));
  border: 1px solid rgba(29, 106, 115, 0.18);
  text-align: center;
`;

const KeywordHeroLabel = styled.div`
  color: #5d757a;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const KeywordHeroWord = styled.div`
  margin-top: 0.35rem;
  color: #0f3338;
  font-size: clamp(1.55rem, 5vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const KeywordGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(5.2rem, 1fr));
  gap: 0.45rem;
  margin-top: 0.85rem;
`;

const KeywordChip = styled.button`
  border: 1.5px solid ${({ $on }) => ($on ? '#1d6a73' : 'rgba(228, 221, 211, 0.95)')};
  background: ${({ $on }) => ($on ? 'rgba(29, 106, 115, 0.14)' : '#fffdf9')};
  color: #12343a;
  border-radius: 0.85rem;
  padding: 0.55rem 0.35rem;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  cursor: ${({ $pickable }) => ($pickable ? 'pointer' : 'default')};
  box-shadow: ${({ $on }) => ($on ? '0 0 0 3px rgba(29, 106, 115, 0.12)' : 'none')};

  &:disabled {
    opacity: ${({ $pickable }) => ($pickable ? 0.55 : 1)};
  }
`;

const SpeakBanner = styled.div`
  margin-top: 0.9rem;
  padding: 1rem;
  border-radius: 1.1rem;
  background: ${({ $mine }) => (
    $mine
      ? 'linear-gradient(145deg, rgba(225, 90, 40, 0.16), rgba(255, 248, 235, 0.95))'
      : 'linear-gradient(145deg, rgba(29, 106, 115, 0.12), rgba(255, 248, 235, 0.95))'
  )};
  border: 1px solid ${({ $mine }) => ($mine ? 'rgba(200, 90, 40, 0.28)' : 'rgba(29, 106, 115, 0.16)')};
  text-align: center;
`;

const SpeakName = styled.div`
  margin-top: 0.25rem;
  color: #0f3338;
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const WordFlash = styled.div`
  margin-top: 0.85rem;
  padding: 0.95rem 1rem;
  border-radius: 1.05rem;
  text-align: center;
  background: linear-gradient(145deg, rgba(29, 106, 115, 0.16), rgba(255, 248, 235, 0.96));
  border: 1px solid rgba(29, 106, 115, 0.22);
  animation: ${wordFlashIn} 2s ease forwards;
`;

const WordFlashLabel = styled.div`
  color: #5d757a;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const WordFlashWord = styled.div`
  margin-top: 0.28rem;
  color: #0f3338;
  font-size: clamp(1.45rem, 4.8vw, 2rem);
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const SpeakOrderBlock = styled.div`
  margin-top: 0.9rem;
  padding: 0.85rem 0.9rem;
  border-radius: 1rem;
  background: rgba(255, 253, 249, 0.92);
  border: 1px solid rgba(228, 221, 211, 0.95);
`;

const SpeakOrderTitle = styled.div`
  color: #5d757a;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const SpeakOrderRound = styled.div`
  margin-top: 0.7rem;
  color: #1d6a73;
  font-size: 0.78rem;
  font-weight: 800;
`;

const SpeakOrderList = styled.ol`
  list-style: none;
  margin: 0.45rem 0 0;
  padding: 0;
  display: grid;
  gap: 0.35rem;
`;

const SpeakOrderItem = styled.li`
  display: grid;
  grid-template-columns: 1.4rem 1.6rem 1fr auto;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  border-radius: 0.75rem;
  background: ${({ $current, $done }) => (
    $current
      ? 'rgba(225, 90, 40, 0.14)'
      : $done
        ? 'rgba(29, 106, 115, 0.06)'
        : 'rgba(243, 239, 232, 0.7)'
  )};
  color: ${({ $done }) => ($done ? '#6e7f83' : '#12343a')};
  font-size: 0.84rem;
  font-weight: 700;
`;

const SpeakOrderNum = styled.span`
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: #5d757a;
`;

const SpeakOrderFace = styled.span`
  width: 1.55rem;
  height: 1.55rem;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: #fffdf9;
  font-size: 0.9rem;
  line-height: 1;
`;

const SpeakOrderBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  color: ${({ $current, $done }) => (
    $current ? '#c24a16' : $done ? '#1d6a73' : '#8a8176'
  )};
`;

const WordBag = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.7rem;
`;

const WordChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.28rem 0.62rem;
  border: 0;
  border-radius: 999px;
  background: ${({ $mine, $voided }) => (
    $voided
      ? 'rgba(150, 70, 70, 0.12)'
      : $mine
        ? 'rgba(29, 106, 115, 0.14)'
        : '#f3efe8'
  )};
  color: ${({ $voided }) => ($voided ? '#8a4a4a' : '#12343a')};
  font-weight: 700;
  font-size: 0.88rem;
  text-decoration: ${({ $voided }) => ($voided ? 'line-through' : 'none')};
  cursor: ${({ $actionable }) => ($actionable ? 'pointer' : 'default')};

  &:disabled {
    opacity: 1;
  }
`;

const VoidTag = styled.em`
  font-style: normal;
  font-size: 0.68rem;
  font-weight: 800;
  text-decoration: none;
`;

const AnswerForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.85rem;

  ${Input} {
    flex: 1;
    min-width: 8rem;
    width: auto;
  }
`;

const StepRail = styled.ol`
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.4rem;
`;

const StepItem = styled.li`
  display: grid;
  gap: 0.28rem;
  justify-items: start;
  min-width: 0;
  opacity: ${({ $dim }) => ($dim ? 0.42 : 1)};
`;

const StepIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  color: ${({ $on, $done }) => ($on || $done ? '#fffdf9' : '#5d736f')};
  background: ${({ $on, $done }) => (
    $done ? '#2f6f78' : $on ? '#1d6a73' : 'rgba(29, 106, 115, 0.12)'
  )};
`;

const StepLabel = styled.span`
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ $on, $done }) => ($on || $done ? '#12343a' : '#6b7f7c')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GameCatalog = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.95rem;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const GameTile = styled.button`
  appearance: none;
  text-align: left;
  border: 1.5px solid ${({ $on }) => ($on ? 'rgba(29, 106, 115, 0.55)' : 'rgba(29, 106, 115, 0.12)')};
  background:
    linear-gradient(160deg, rgba(255, 253, 249, 0.98), rgba(241, 247, 246, 0.92)),
    ${({ $on }) => ($on ? 'rgba(29, 106, 115, 0.08)' : 'transparent')};
  border-radius: 1rem;
  padding: 0.95rem 0.9rem 0.9rem;
  cursor: pointer;
  color: #12343a;
  box-shadow: ${({ $on }) => ($on ? '0 10px 22px rgba(18, 52, 58, 0.12)' : '0 6px 16px rgba(18, 52, 58, 0.05)')};
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
  min-height: 7.4rem;
  display: grid;
  align-content: space-between;
  gap: 0.7rem;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(29, 106, 115, 0.34);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }
`;

const GameTileMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 0.8rem;
  background: rgba(29, 106, 115, 0.1);
  font-size: 1.15rem;
  line-height: 1;
`;

const GameTileCopy = styled.span`
  display: grid;
  gap: 0.18rem;
`;

const GameTileTitle = styled.span`
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const GameTileMeta = styled.span`
  color: #5d736f;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.35;
`;

const SetupPanel = styled.div`
  margin-top: 0.95rem;
  padding: 0.95rem 0.95rem 1rem;
  border-radius: 1rem;
  background: rgba(255, 253, 249, 0.72);
  border: 1px solid rgba(29, 106, 115, 0.12);
`;

const SetupTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
`;

const SetupKicker = styled.p`
  margin: 0;
  color: #2f6f78;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SetupSection = styled.section`
  margin-top: 0.95rem;

  & + & {
    margin-top: 1rem;
    padding-top: 0.9rem;
    border-top: 1px solid rgba(29, 106, 115, 0.1);
  }
`;

const SetupSectionTitle = styled.h4`
  margin: 0 0 0.45rem;
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #12343a;
`;

const SetupHint = styled.p`
  margin: 0.35rem 0 0;
  color: #6b7f7c;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.45;
`;

const HOST_GAME_OPTIONS = [
  {
    id: 'chosung',
    mark: 'ㅊ',
    title: '초성 퀴즈',
    meta: '팀 대결 · 단어 맞히기',
    needsMembers: false,
    needsTeams: true,
    minMembers: 0,
    help: (game) => `팀 멤버 수 × ${game.wordsPerMember || 2}개 단어를 먼저 맞히면, 관리자와 다른 팀이 확인한 뒤에 +${game.points || 10}점이 들어갑니다.`,
  },
  {
    id: 'vote',
    mark: '투',
    title: '투표',
    meta: '전원 투표 · 즉시 공개',
    needsMembers: false,
    needsTeams: false,
    minMembers: 2,
    help: () => '시작하면 게스트에게 “한 명에게 투표하세요”가 뜹니다. 모두 투표하면 누가 몇 표인지 바로 공개됩니다.',
  },
  {
    id: 'hunmin',
    mark: '훈',
    title: '훈민정음',
    meta: '초성 릴레이 · 탈락제',
    needsMembers: true,
    needsTeams: false,
    minMembers: 2,
    help: () => '초성 두 글자에 맞는 단어를 순서대로 전송합니다. 탈락자가 나올 때까지 같은 초성으로 계속 돌아가며, 같은 라운드에서 쓴 단어는 다시 쓸 수 없습니다. 탈락자는 최종 1위가 나오거나 게임이 끝날 때까지 재참가하지 않습니다. 관리자는 언제든 임의 탈락이 가능합니다.',
  },
  {
    id: 'liar',
    mark: '라',
    title: '라이어 게임',
    meta: '제시어 추리 · 3인+',
    needsMembers: true,
    needsTeams: false,
    minMembers: 3,
    help: () => '참가자를 고르면 시민에게는 제시어가, 라이어에게는 25개 키워드가 갑니다. 각자 2번씩 발언 후 투표하고, 라이어가 지목되면 제시어를 맞혀야 합니다. 팀 점수는 없습니다.',
  },
  {
    id: 'draw',
    mark: '뽑',
    title: '랜덤 뽑기',
    meta: '룰렛 · 한 명 뽑기',
    needsMembers: true,
    needsTeams: false,
    minMembers: 2,
    help: () => '참가자를 고른 뒤 실행하면, 모든 화면에 룰렛이 돌아갑니다.',
  },
];

const SessionHelpHeader = ({ id, title, as = 'h2', help, openId, onToggle, compact = false }) => {
  const open = openId === id;
  return (
    <>
      <TitleRow>
        <Title as={as} $onCard $compact={compact}>{title}</Title>
        {help ? (
          <HelpButton
            type="button"
            aria-label={`${title} 설명`}
            aria-expanded={open}
            $on={open}
            onClick={() => onToggle(id)}
          >
            ?
          </HelpButton>
        ) : null}
      </TitleRow>
      {open && help ? <HelpPanel>{help}</HelpPanel> : null}
    </>
  );
};

const VoteResultList = styled.div`
  display: grid;
  gap: 0.55rem;
  margin-top: 0.85rem;
`;

const VoteResultRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.7rem;
  align-items: center;
  padding: 0.7rem 0.8rem;
  border-radius: 0.9rem;
  background: ${({ $win }) => ($win ? 'rgba(29, 106, 115, 0.12)' : 'rgba(255, 253, 249, 0.92)')};
  border: 1px solid ${({ $win }) => ($win ? 'rgba(29, 106, 115, 0.28)' : 'rgba(230, 220, 207, 0.8)')};
`;

const VotePerson = styled.div`
  min-width: 0;
`;

const VoteMeter = styled.div`
  height: 0.42rem;
  margin-top: 0.38rem;
  border-radius: 999px;
  background: rgba(29, 106, 115, 0.1);
  overflow: hidden;
`;

const VoteMeterFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background: ${({ $win }) => ($win ? '#1d6a73' : '#c4b49a')};
`;

const VoteCount = styled.span`
  font-size: 1.15rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #12343a;
  white-space: nowrap;
`;

const WaitBadge = styled.span`
  color: ${({ $on }) => ($on ? '#1d6a73' : '#8a8176')};
`;

const WaitPanel = styled.div`
  margin-top: 0.9rem;
  padding: 1.2rem 1rem 1.1rem;
  border-radius: 1.1rem;
  background: rgba(29, 106, 115, 0.08);
  text-align: center;
`;

const WaitFace = styled.p`
  margin: 0;
  font-size: 3.1rem;
  line-height: 1;
  animation: ${waitPulse} 1.35s ease-in-out infinite;
`;

const WaitTitle = styled.h3`
  margin: 0.45rem 0 0;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #12343a;
`;

const WaitCopy = styled.p`
  margin: 0.35rem 0 0;
  color: #4d6a70;
  font-weight: 600;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const WaitDots = styled.p`
  margin: 0.55rem 0 0;
  color: #1d6a73;
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  animation: ${waitPulse} 1.35s ease-in-out infinite;
`;


const MemberRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.55rem;
`;

const MemberChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  padding: 0.18rem 0.5rem 0.18rem 0.28rem;
  border-radius: 999px;
  background: rgba(29, 106, 115, 0.08);
  color: #3d555a;
  font-size: 0.78rem;
  font-weight: 600;
`;

const PickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(5.6rem, 1fr));
  gap: 0.55rem;
`;

const PersonPick = styled.button`
  display: grid;
  justify-items: center;
  gap: 0.3rem;
  border: 1.5px solid ${({ $on, $taken }) => (
    $taken ? '#e4ddd3' : $on ? '#1d6a73' : 'rgba(228, 221, 211, 0.9)'
  )};
  background: ${({ $on, $taken }) => (
    $taken ? '#f3efe8' : $on ? 'rgba(29, 106, 115, 0.12)' : '#fffdf9'
  )};
  color: ${({ $taken }) => ($taken ? '#8a8176' : '#12343a')};
  border-radius: 1.1rem;
  padding: 0.7rem 0.3rem 0.55rem;
  cursor: ${({ $taken }) => ($taken ? 'not-allowed' : 'pointer')};
  box-shadow: ${({ $on, $taken }) => (
    $taken ? 'none' : $on ? '0 0 0 3px rgba(29, 106, 115, 0.12)' : '0 6px 14px rgba(8, 28, 40, 0.05)'
  )};
  opacity: ${({ $taken }) => ($taken ? 0.62 : 1)};
`;

const PersonFace = styled.span`
  width: 2.7rem;
  height: 2.7rem;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? '#dff3f4' : '#f4efe6')};
  font-size: 1.35rem;
  line-height: 1;
`;

const PersonName = styled.span`
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const PersonState = styled.span`
  font-size: 0.64rem;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#1d6a73' : '#8a8176')};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0.85rem;
  color: #12343a;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.35;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

  input {
    width: 1.05rem;
    height: 1.05rem;
    margin-top: 0.12rem;
    flex-shrink: 0;
    accent-color: #1d6a73;
  }
`;

const renderIconPopover = ({ selectedId, onSelect }) => (
  <IconPopover>
    {VACATION_ICONS.map((item) => (
      <IconOnlyPick
        key={item.id}
        type="button"
        $active={selectedId === item.id}
        onClick={() => onSelect(item.id)}
        aria-label={item.emoji}
      >
        {item.emoji}
      </IconOnlyPick>
    ))}
  </IconPopover>
);

const DrawRoulette = ({ game }) => {
  const candidates = game?.candidates || [];
  const count = Math.max(candidates.length, 1);
  const slice = 360 / count;
  const winnerIndex = Math.max(0, candidates.findIndex((person) => person.userId === game?.winnerUserId));
  const target = (6 * 360) - ((winnerIndex + 0.5) * slice);
  const [deg, setDeg] = useState(0);
  const [done, setDone] = useState(false);
  const lastTick = useRef(-1);
  const heardWin = useRef(false);

  useEffect(() => {
    const startedAt = Date.parse(game?.startedAt || '');
    const spinMs = Number(game?.spinMs) || 6800;
    const started = Number.isFinite(startedAt) ? startedAt : Date.now();
    lastTick.current = -1;
    heardWin.current = false;
    const elapsedNow = Date.now() - started;
    if (elapsedNow >= spinMs) {
      setDeg(target);
      setDone(true);
      return undefined;
    }
    setDone(false);
    let raf = 0;
    const tick = () => {
      const elapsed = Date.now() - started;
      const progress = Math.min(1, Math.max(0, elapsed / spinMs));
      const rotation = target * easeOutCubic(progress);
      setDeg(rotation);
      const atTop = (360 - (rotation % 360) + 360) % 360;
      const index = Math.floor(atTop / slice) % count;
      if (index !== lastTick.current && progress < 1) {
        lastTick.current = index;
        playDrawTick();
      }
      if (progress >= 1) {
        setDone(true);
        if (!heardWin.current) {
          heardWin.current = true;
          playDrawWin();
        }
        return;
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [count, game?.startedAt, game?.spinMs, game?.winnerUserId, slice, target]);

  const gradient = candidates.map((_, index) => {
    const color = DRAW_COLORS[index % DRAW_COLORS.length];
    return `${color} ${index * slice}deg ${(index + 1) * slice}deg`;
  }).join(', ');
  const winner = candidates[winnerIndex] || null;
  const winnerIcon = getVacationIcon(winner?.icon);

  return (
    <>
      <WheelWrap>
        <WheelPointer />
        <WheelDisc
          $deg={deg}
          $bg={`conic-gradient(${gradient || '#1d6a73 0deg 360deg'})`}
        >
          {candidates.map((person, index) => (
            <WheelLabel key={person.userId} $angle={(index + 0.5) * slice}>
              <span>{getVacationIcon(person.icon)?.emoji || '🙂'}</span>
              <span>{person.name}</span>
            </WheelLabel>
          ))}
        </WheelDisc>
        <WheelHub>🎲</WheelHub>
      </WheelWrap>
      {done && winner ? (
        <DrawWinCard>
          {['🎉', '✨', '🎊', '⭐', '🎈', '🥳'].map((bit, index) => (
            <ConfettiBit
              key={`${bit}-${index}`}
              $left={`${8 + index * 15}%`}
              $delay={`${index * 0.06}s`}
            >
              {bit}
            </ConfettiBit>
          ))}
          <Eyebrow $onCard>Winner</Eyebrow>
          <LockFace>{winnerIcon?.emoji || '🙂'}</LockFace>
          <LockName>{winner.name}</LockName>
          <LockTeam>
            {winner.teamName ? `${getVacationIcon(winner.teamIcon)?.emoji || ''} ${winner.teamName}` : '당첨!'}
          </LockTeam>
        </DrawWinCard>
      ) : null}
    </>
  );
};

const getBuzzerCopy = ({ myTeam, locked, buzzerReady, buzzerOpen, meId }) => {
  if (!myTeam) {
    return { text: '대기', hint: '팀에 배정되면 부저를 사용할 수 있습니다.' };
  }
  if (locked) {
    const mine = locked.userId === meId;
    return mine
      ? { text: '선점', hint: '당신이 먼저입니다. 답변하세요.' }
      : { text: '놓침', hint: `${locked.name} 님이 먼저 눌렀습니다.` };
  }
  if (buzzerReady) {
    return { text: 'Buzz', hint: '지금입니다.' };
  }
  if (buzzerOpen) {
    return { text: '준비', hint: '신호가 열렸습니다.' };
  }
  return { text: '대기', hint: '호스트가 신호를 줄 때까지 기다려 주세요.' };
};

const RecreationPage = () => {
  const history = useHistory();
  const location = useLocation();
  const isAdminPage = (location.pathname || '').startsWith('/portal/recreation/admin');
  const { request, loading } = useApi();
  const [portalUser, setPortalUser] = useState(() => readPortalUser());
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [setupLoginId, setSetupLoginId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mode, setMode] = useState('login');
  const [message, setMessage] = useState('');
  const [state, setState] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [pressed, setPressed] = useState(false);
  const [chosungWord, setChosungWord] = useState('');
  const [chosungError, setChosungError] = useState('');
  const [allowSelfVote, setAllowSelfVote] = useState(false);
  const [selectedVoteUserId, setSelectedVoteUserId] = useState(null);
  const [drawUserIds, setDrawUserIds] = useState(null);
  const [mafiaUserIds, setMafiaUserIds] = useState(null);
  const [selectedMafiaVoteUserId, setSelectedMafiaVoteUserId] = useState(null);
  const [selectedMafiaGuess, setSelectedMafiaGuess] = useState('');
  const [hunminUserIds, setHunminUserIds] = useState(null);
  const [hunminWord, setHunminWord] = useState('');
  const [hunminFlashWord, setHunminFlashWord] = useState('');
  const hunminSeenSubmitRef = useRef({ key: '', initialized: false });
  const [myTeamNameDraft, setMyTeamNameDraft] = useState('');
  const [openPicker, setOpenPicker] = useState(null);
  const [openHelpId, setOpenHelpId] = useState(null);
  const [hostSetupGame, setHostSetupGame] = useState(null);
  const identityRef = useRef(null);
  const heardLockId = useRef(null);
  const soundReady = useRef(false);
  const pendingMafiaSpeakRef = useRef(null);
  const mafiaSpeakBusyRef = useRef(false);
  const [mafiaSpeakBusy, setMafiaSpeakBusy] = useState(false);

  const applyState = useCallback((payload) => {
    if (!payload?.teams) return;
    const pending = pendingMafiaSpeakRef.current;
    if (pending && (payload.game?.type === 'liar' || payload.game?.type === 'mafia')) {
      const serverIndex = Number(payload.game.speakIndex || 0);
      const stillSpeaking = payload.game.status === 'speaking';
      if (stillSpeaking && serverIndex < pending.speakIndex) {
        setState((prev) => {
          if (!prev?.game || (prev.game.type !== 'liar' && prev.game.type !== 'mafia')) {
            return {
              ...payload,
              game: {
                ...payload.game,
                speakIndex: pending.speakIndex,
              },
            };
          }
          return {
            ...payload,
            game: prev.game,
          };
        });
        return;
      }
      if (!stillSpeaking || serverIndex >= pending.speakIndex) {
        pendingMafiaSpeakRef.current = null;
      }
    }
    setState(payload);
  }, []);

  const fetchState = useCallback(async (user = portalUser, { silent = false } = {}) => {
    if (!user?.login_id) return;
    try {
      const response = await fetch(
        `${BACKEND_BASE_URL}/api/recreation/state?login_id=${encodeURIComponent(user.login_id)}`
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || '상태를 불러오지 못했습니다.');
      }
      applyState(data);
      if (!silent) setMessage('');
    } catch (err) {
      if (!silent) setMessage(err?.message || '상태를 불러오지 못했습니다.');
    }
  }, [applyState, portalUser]);

  useEffect(() => {
    const unlock = () => unlockBuzzerAudio();
    window.addEventListener('pointerdown', unlock);
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (!openPicker) return undefined;
    const onPointerDown = (event) => {
      if (identityRef.current && !identityRef.current.contains(event.target)) {
        setOpenPicker(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openPicker]);

  const toggleHelp = useCallback((id) => {
    setOpenHelpId((prev) => (prev === id ? null : id));
  }, []);

  const gamePollFast = Boolean(
    ((state?.game?.type === 'liar' || state?.game?.type === 'mafia')
      && ['speaking', 'voting', 'guessing'].includes(state?.game?.status))
    || (state?.game?.type === 'hunmin' && state?.game?.status === 'running')
  );

  useEffect(() => {
    if (!portalUser) return undefined;
    // 응답이 끝난 뒤에 다음 폴링을 예약해야 서버가 느릴 때 요청이 겹쳐 쌓이지 않는다.
    let cancelled = false;
    let timer = null;
    const interval = gamePollFast ? 500 : 1000;

    const loop = async (silent) => {
      if (cancelled) return;
      await fetchState(portalUser, { silent });
      if (cancelled) return;
      timer = window.setTimeout(() => loop(true), interval);
    };

    loop(false);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [fetchState, portalUser, gamePollFast]);

  useEffect(() => {
    const current = state?.game;
    if (!current || current.type !== 'hunmin') {
      hunminSeenSubmitRef.current = { key: '', initialized: false };
      setHunminFlashWord('');
      return undefined;
    }
    const word = String(current.lastSubmittedWord || '').trim();
    const usedCount = Number(current.usedWordCount || 0);
    const key = usedCount > 0 && word ? `${usedCount}:${word}` : '0:';
    const previous = hunminSeenSubmitRef.current;
    if (!previous.initialized) {
      hunminSeenSubmitRef.current = { key, initialized: true };
      return undefined;
    }
    if (key === previous.key || usedCount <= 0 || !word) return undefined;
    hunminSeenSubmitRef.current = { key, initialized: true };
    setHunminFlashWord(word);
    const timer = window.setTimeout(() => setHunminFlashWord(''), 2000);
    return () => window.clearTimeout(timer);
  }, [state?.game]);

  useEffect(() => {
    if (!isAdminPage || !portalUser?.login_id) return undefined;
    let cancelled = false;
    const loadStudents = async () => {
      try {
        const response = await fetch(
          `${BACKEND_BASE_URL}/api/recreation/students?login_id=${encodeURIComponent(portalUser.login_id)}`
        );
        const data = await response.json();
        if (!cancelled && response.ok) {
          setStudents(data?.students || []);
        }
      } catch {
        // ignore
      }
    };
    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [isAdminPage, portalUser?.login_id]);

  useEffect(() => {
    if (state?.game?.type === 'vote' && state.game.status === 'running') {
      setAllowSelfVote(Boolean(state.game.allowSelfVote));
      return;
    }
    setSelectedVoteUserId(null);
  }, [state?.game?.type, state?.game?.status, state?.game?.allowSelfVote]);

  const postAction = async (endpoint, body = {}) => {
    if (!portalUser?.login_id) return;
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}${endpoint}`,
        method: 'POST',
        body: { login_id: portalUser.login_id, ...body },
      });
      applyState(data);
      return data;
    } catch (err) {
      setMessage(err?.responseData?.message || '요청에 실패했습니다.');
      return null;
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/auth/login`,
        method: 'POST',
        body: { login_id: loginId.trim(), password },
      });
      if (data?.status === 'password_setup_required') {
        setMode('setup');
        setSetupLoginId(data.login_id || loginId.trim());
        setPassword('');
        setMessage('첫 입장이라 비밀번호를 설정해 주세요.');
        return;
      }
      if (!data?.user) {
        setMessage('로그인 응답이 올바르지 않습니다.');
        return;
      }
      savePortalUser(data.user);
      setPortalUser(data.user);
    } catch (err) {
      setMessage(err?.responseData?.message || '로그인에 실패했습니다.');
    }
  };

  const handleSetupPassword = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/auth/setup-password`,
        method: 'POST',
        body: {
          login_id: setupLoginId.trim(),
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      });
      if (!data?.user) {
        setMessage('비밀번호 설정 응답이 올바르지 않습니다.');
        return;
      }
      savePortalUser(data.user);
      setPortalUser(data.user);
    } catch (err) {
      setMessage(err?.responseData?.message || '비밀번호 설정에 실패했습니다.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PORTAL_USER_KEY);
    window.dispatchEvent(new Event(PORTAL_USER_UPDATED_EVENT));
    setPortalUser(null);
    setState(null);
    setMode('login');
    setPassword('');
    setMessage('');
  };

  const pressBuzzer = async () => {
    unlockBuzzerAudio();
    setPressed(true);
    window.setTimeout(() => setPressed(false), 220);
    await postAction('/api/recreation/buzzer/press');
  };

  const openMemberEditor = async (team) => {
    if (editingTeamId === team.id) {
      setEditingTeamId(null);
      return;
    }
    setEditingTeamId(team.id);
    setSelectedUserIds(team.members.map((member) => member.userId));
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/recreation/students?login_id=${encodeURIComponent(portalUser.login_id)}`,
      });
      setStudents(data?.students || []);
    } catch (err) {
      setMessage(err?.responseData?.message || '명단을 불러오지 못했습니다.');
    }
  };

  const saveMembers = async () => {
    const data = await postAction(`/api/recreation/teams/${editingTeamId}/members`, {
      userIds: selectedUserIds,
    });
    if (data) setEditingTeamId(null);
  };

  const createTeam = async (event) => {
    event.preventDefault();
    const data = await postAction('/api/recreation/teams', { name: teamName.trim() });
    if (data) setTeamName('');
  };

  const setMyIcon = async (icon) => {
    await postAction('/api/recreation/me/icon', { icon });
    setOpenPicker(null);
  };

  const setTeamIcon = async (teamId, icon) => {
    await postAction(`/api/recreation/teams/${teamId}/icon`, { icon });
    setOpenPicker(null);
  };

  const openTeamNameEditor = () => {
    if (!myTeam) return;
    setMyTeamNameDraft(myTeam.name || '');
    setOpenPicker((prev) => (prev === 'teamName' ? null : 'teamName'));
  };

  const saveTeamName = async (event) => {
    event.preventDefault();
    const name = myTeamNameDraft.trim();
    if (!myTeam || !name || loading) return;
    const data = await postAction(`/api/recreation/teams/${myTeam.id}/name`, { name });
    if (data) setOpenPicker(null);
  };

  const startChosungGame = async () => {
    await postAction('/api/recreation/games/chosung/start');
    setChosungWord('');
    setChosungError('');
  };

  const startVoteGame = async () => {
    setSelectedVoteUserId(null);
    await postAction('/api/recreation/games/vote/start', { allowSelfVote });
  };

  const startDrawGame = async (userIds) => {
    const ids = (Array.isArray(userIds) ? userIds : []).filter(Boolean);
    if (ids.length < 2) {
      setMessage('뽑으려면 참가자를 두 명 이상 골라 주세요.');
      return;
    }
    setMessage('');
    await postAction('/api/recreation/games/draw/start', { userIds: ids });
  };

  const startMafiaGame = async (userIds) => {
    const ids = (Array.isArray(userIds) ? userIds : []).filter(Boolean);
    if (ids.length < 3) {
      setMessage('라이어 게임은 참가자를 세 명 이상 골라 주세요.');
      return;
    }
    setMessage('');
    setSelectedMafiaVoteUserId(null);
    setSelectedMafiaGuess('');
    await postAction('/api/recreation/games/liar/start', { userIds: ids });
  };

  const startHunminGame = async (userIds) => {
    const ids = (Array.isArray(userIds) ? userIds : []).filter(Boolean);
    if (ids.length < 2) {
      setMessage('훈민정음은 참가자를 두 명 이상 골라 주세요.');
      return;
    }
    setHunminWord('');
    await postAction('/api/recreation/games/hunmin/start', { userIds: ids });
  };

  const submitHunminWord = async (event) => {
    event.preventDefault();
    const word = hunminWord.trim();
    if (!word || loading) return;
    const data = await postAction('/api/recreation/games/hunmin/answer', { word });
    if (data) {
      setHunminWord('');
      if (data.answerRejected) {
        setMessage(data.failureMessage || '탈락했습니다.');
      }
    }
  };

  const passHunmin = async () => {
    if (!window.confirm('포기하면 바로 탈락합니다. 포기할까요?')) return;
    await postAction('/api/recreation/games/hunmin/pass');
  };

  const eliminateHunminPlayer = async (userId, name) => {
    if (!window.confirm(`${name} 님을 탈락시킬까요? 남은 인원으로 다음 라운드가 시작됩니다.`)) return;
    await postAction('/api/recreation/games/hunmin/eliminate', { userId });
  };

  const advanceMafiaSpeak = async () => {
    if (!portalUser?.login_id || mafiaSpeakBusyRef.current) return;
    const currentGame = state?.game;
    if (!currentGame || (currentGame.type !== 'liar' && currentGame.type !== 'mafia') || currentGame.status !== 'speaking') return;
    if (!currentGame.canAdvanceSpeak) return;

    const nextIndex = Math.min(
      (currentGame.speakIndex || 0) + 1,
      currentGame.speaksTotal || (currentGame.speakOrder || []).length
    );
    const done = nextIndex >= (currentGame.speaksTotal || (currentGame.speakOrder || []).length);
    const meId = state?.me?.id;
    const isHost = Boolean(state?.isHost);

    mafiaSpeakBusyRef.current = true;
    setMafiaSpeakBusy(true);
    pendingMafiaSpeakRef.current = { speakIndex: nextIndex };
    setMessage('');

    setState((prev) => {
      if (!prev?.game || (prev.game.type !== 'liar' && prev.game.type !== 'mafia') || prev.game.status !== 'speaking') return prev;
      const speakOrder = (prev.game.speakOrder || []).map((item, index) => ({
        ...item,
        done: index < nextIndex || done,
        current: !done && index === nextIndex,
      }));
      const nextSpeakerItem = !done ? speakOrder[nextIndex] : null;
      const currentSpeaker = nextSpeakerItem
        ? {
          userId: nextSpeakerItem.userId,
          name: nextSpeakerItem.name,
          icon: nextSpeakerItem.icon,
          isMe: Boolean(meId && nextSpeakerItem.userId === meId),
        }
        : null;
      return {
        ...prev,
        game: {
          ...prev.game,
          speakIndex: nextIndex,
          speaksDone: Math.min(nextIndex, prev.game.speaksTotal || 0),
          status: done ? 'voting' : 'speaking',
          speakOrder,
          currentSpeaker,
          canAdvanceSpeak: Boolean(
            !done
            && currentSpeaker
            && (isHost || currentSpeaker.isMe)
          ),
          canVote: Boolean(done && prev.game.myRole),
          votedCount: done ? 0 : prev.game.votedCount,
          totalVoters: done
            ? (prev.game.players || prev.game.candidates || []).length
            : prev.game.totalVoters,
          myVoteUserId: done ? null : prev.game.myVoteUserId,
          candidates: done
            ? (prev.game.players || prev.game.candidates || []).map((person) => ({
              ...person,
              voted: false,
              votes: null,
              accused: false,
              winner: false,
            }))
            : prev.game.candidates,
        },
      };
    });

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/recreation/games/liar/speak-done`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_id: portalUser.login_id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        pendingMafiaSpeakRef.current = null;
        setMessage(data?.message || '발언을 완료하지 못했습니다.');
        await fetchState(portalUser, { silent: true });
        return;
      }
      applyState(data);
    } catch (err) {
      pendingMafiaSpeakRef.current = null;
      setMessage(err?.message || '발언을 완료하지 못했습니다.');
      await fetchState(portalUser, { silent: true });
    } finally {
      mafiaSpeakBusyRef.current = false;
      setMafiaSpeakBusy(false);
    }
  };

  const submitMafiaVote = async () => {
    if (!selectedMafiaVoteUserId || loading) return;
    const data = await postAction('/api/recreation/games/liar/vote', {
      targetUserId: selectedMafiaVoteUserId,
    });
    if (data) setSelectedMafiaVoteUserId(null);
  };

  const submitMafiaGuess = async () => {
    if (!selectedMafiaGuess || loading) return;
    const data = await postAction('/api/recreation/games/liar/guess', {
      guess: selectedMafiaGuess,
    });
    if (data) setSelectedMafiaGuess('');
  };

  const revealVote = async () => {
    await postAction('/api/recreation/games/vote/reveal');
  };

  const submitVote = async () => {
    if (!selectedVoteUserId || loading) return;
    const data = await postAction('/api/recreation/games/vote/cast', {
      targetUserId: selectedVoteUserId,
    });
    if (data) setSelectedVoteUserId(null);
  };

  const stopGame = async () => {
    await postAction('/api/recreation/games/stop');
    setChosungWord('');
    setChosungError('');
  };

  const voidChosungWord = async (teamId, word) => {
    if (!window.confirm(`「${word}」를 무효 처리할까요?`)) return;
    await postAction('/api/recreation/games/chosung/void', { teamId, word });
  };

  const confirmChosungWin = async () => {
    await postAction('/api/recreation/games/chosung/confirm');
  };

  const submitChosung = async (event) => {
    event.preventDefault();
    const word = chosungWord.trim().replace(/\s/g, '');
    if (!word || loading) return;
    if (word.length !== 2) {
      setChosungError('두 글자 단어만 입력해 주세요.');
      return;
    }
    setChosungError('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/recreation/games/chosung/answer`,
        method: 'POST',
        body: { login_id: portalUser.login_id, word },
      });
      applyState(data);
      if (data?.answerAccepted) {
        setChosungWord('');
        setChosungError('');
        return;
      }
      setChosungError('이 단어는 쓸 수 없어요.');
    } catch (err) {
      setChosungError(err?.responseData?.message || '이 단어는 쓸 수 없어요.');
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('이 팀을 해산할까요?')) return;
    if (!portalUser?.login_id) return;
    setMessage('');
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/recreation/teams/${teamId}?login_id=${encodeURIComponent(portalUser.login_id)}`,
        method: 'DELETE',
      });
      applyState(data);
    } catch (err) {
      setMessage(err?.responseData?.message || '팀을 삭제하지 못했습니다.');
    }
  };

  const resetGame = async () => {
    if (!window.confirm('점수와 팀을 전부 초기화할까요?')) return;
    await postAction('/api/recreation/reset');
  };

  const sortedTeams = useMemo(() => {
    const teams = state?.teams || [];
    return [...teams].sort((a, b) => b.score - a.score || a.sortOrder - b.sortOrder);
  }, [state]);

  const memberIconByUserId = useMemo(() => {
    const icons = {};
    (state?.teams || []).forEach((team) => {
      team.members.forEach((member) => {
        if (member.icon) icons[member.userId] = member.icon;
      });
    });
    return icons;
  }, [state]);

  const memberTeamByUserId = useMemo(() => {
    const teams = {};
    (state?.teams || []).forEach((team) => {
      team.members.forEach((member) => {
        teams[member.userId] = { teamId: team.id, teamName: team.name };
      });
    });
    return teams;
  }, [state]);

  const rosterPeople = useMemo(() => {
    const people = [];
    const seen = new Set();
    (state?.teams || []).forEach((team) => {
      team.members.forEach((member) => {
        if (seen.has(member.userId)) return;
        seen.add(member.userId);
        people.push({
          ...member,
          teamName: team.name,
          teamIcon: team.icon,
        });
      });
    });
    students.forEach((student) => {
      if (seen.has(student.userId)) return;
      seen.add(student.userId);
      people.push({
        userId: student.userId,
        name: student.name,
        icon: student.icon || '',
        teamName: '',
        teamIcon: '',
      });
    });
    return people;
  }, [state, students]);

  const selectedDrawIds = useMemo(() => {
    const allIds = rosterPeople.map((person) => person.userId);
    if (drawUserIds == null) return allIds;
    const valid = new Set(allIds);
    return drawUserIds.filter((id) => valid.has(id));
  }, [drawUserIds, rosterPeople]);

  const selectedMafiaIds = useMemo(() => {
    const allIds = rosterPeople.map((person) => person.userId);
    if (mafiaUserIds == null) return allIds;
    const valid = new Set(allIds);
    return mafiaUserIds.filter((id) => valid.has(id));
  }, [mafiaUserIds, rosterPeople]);

  const selectedHunminIds = useMemo(() => {
    const allIds = rosterPeople.map((person) => person.userId);
    if (hunminUserIds == null) return allIds;
    const valid = new Set(allIds);
    return hunminUserIds.filter((id) => valid.has(id));
  }, [hunminUserIds, rosterPeople]);

  const myTeam = (state?.teams || []).find((team) => team.id === state?.me?.teamId);
  const game = state?.game || { status: 'idle' };
  const gameBusy = ['running', 'reviewing', 'speaking', 'voting', 'guessing'].includes(game.status);
  const rosterCount = (state?.teams || []).reduce((sum, team) => sum + (team.members?.length || 0), 0);
  const chosungActive = game.type === 'chosung' && ['running', 'reviewing', 'finished'].includes(game.status);
  const voteActive = game.type === 'vote' && ['running', 'finished'].includes(game.status);
  const drawActive = game.type === 'draw' && ['running', 'finished'].includes(game.status);
  const mafiaActive = (game.type === 'liar' || game.type === 'mafia') && ['speaking', 'voting', 'guessing', 'finished'].includes(game.status);
  const hunminActive = game.type === 'hunmin' && ['running', 'reviewing', 'finished'].includes(game.status);
  const myVotePerson = (game.candidates || []).find((person) => person.userId === game.myVoteUserId);
  const myMafiaVotePerson = (game.candidates || []).find((person) => person.userId === game.myVoteUserId);
  const mafiaMaxVotes = Math.max(0, ...((game.candidates || []).map((person) => person.votes || 0)));
  const pendingTeam = (state?.teams || []).find((team) => team.id === game.pendingTeamId);
  const winnerTeam = (state?.teams || []).find((team) => team.id === game.winnerTeamId);
  const myGameProgress = myTeam ? game.teams?.[String(myTeam.id)] : null;
  const chosungTargetFor = (team) => {
    if (!team) return 0;
    const fromGame = game.teams?.[String(team.id)]?.target;
    if (Number.isFinite(fromGame) && fromGame > 0) return fromGame;
    return (team.members?.length || 0) * (game.wordsPerMember || 2);
  };
  const myChosungTarget = chosungTargetFor(myTeam);
  const pendingChosungTarget = chosungTargetFor(pendingTeam);
  const toWordEntries = (progress) => (
    (progress?.words || []).map((item) => (
      typeof item === 'string'
        ? { word: item, voided: false, voidedByName: '' }
        : item
    ))
  );
  const canVoidTeamWords = () => (
    Boolean(isAdminPage && state?.isHost && game.type === 'chosung' && ['running', 'reviewing'].includes(game.status))
  );
  const renderVotePicker = () => (
    <>
      <PickGrid style={{ marginTop: '0.85rem' }}>
        {(game.candidates || []).map((person) => {
          const selected = selectedVoteUserId === person.userId;
          const isSelf = person.userId === state?.me?.id;
          const selfBlocked = Boolean(isSelf && !game.allowSelfVote);
          const icon = getVacationIcon(person.icon);
          const canPick = Boolean(
            game.canVote
            && game.status === 'running'
            && !game.myVoteUserId
            && !loading
            && !selfBlocked
          );
          return (
            <PersonPick
              key={person.userId}
              type="button"
              $on={selected}
              $taken={selfBlocked}
              disabled={!canPick}
              onClick={canPick ? () => setSelectedVoteUserId(person.userId) : undefined}
            >
              <PersonFace $on={selected && !selfBlocked}>
                {icon?.emoji || '🙂'}
              </PersonFace>
              <PersonName>{person.name}</PersonName>
              <PersonState $on={selected && !selfBlocked}>
                {selfBlocked ? '나에게 투표 불가' : selected ? '선택됨' : person.teamName || '선택'}
              </PersonState>
            </PersonPick>
          );
        })}
      </PickGrid>
      <PrimaryButton
        type="button"
        $block
        $mt
        disabled={!selectedVoteUserId || loading}
        onClick={submitVote}
      >
        {loading ? '투표 중...' : '투표 완료'}
      </PrimaryButton>
    </>
  );
  const renderVoteWaiting = () => {
    const icon = getVacationIcon(myVotePerson?.icon);
    return (
      <WaitPanel>
        <WaitFace>
          {myVotePerson ? (icon?.emoji || '🙂') : '⏳'}
        </WaitFace>
        <WaitTitle>기다리는 중</WaitTitle>
        <WaitCopy>
          {myVotePerson
            ? `${myVotePerson.name} 님에게 투표했어요. 모두 끝날 때까지 기다려 주세요.`
            : '모두 투표하면 결과가 공개됩니다.'}
        </WaitCopy>
        <ProgressNote>
          {game.votedCount || 0} / {game.totalVoters || 0} 투표
        </ProgressNote>
        <WaitDots aria-hidden="true">···</WaitDots>
      </WaitPanel>
    );
  };
  const renderVoteResults = () => {
    const maxVotes = game.maxVotes || 1;
    return (
      <VoteResultList>
        {(game.candidates || []).map((person, index) => {
          const icon = getVacationIcon(person.icon);
          const votes = person.votes || 0;
          const pct = maxVotes > 0 ? Math.round((votes / maxVotes) * 100) : 0;
          return (
            <VoteResultRow key={person.userId} $win={person.winner}>
              <PersonFace $on={person.winner}>
                {icon?.emoji || '🙂'}
              </PersonFace>
              <VotePerson>
                <PersonName>
                  {index + 1}. {person.name}
                </PersonName>
                <PersonState $on={person.winner}>
                  {person.teamName || ''}
                  {person.winner ? `${person.teamName ? ' · ' : ''}1위` : ''}
                </PersonState>
                <VoteMeter>
                  <VoteMeterFill $pct={pct} $win={person.winner} />
                </VoteMeter>
              </VotePerson>
              <VoteCount>{votes}표</VoteCount>
            </VoteResultRow>
          );
        })}
      </VoteResultList>
    );
  };
  const renderMafiaKeywordPanel = () => {
    if ((game.myRole === 'liar' || game.myRole === 'mafia') || (game.myKeywords && game.myKeywords.length && (game.isMeLiar || game.isMeMafia))) {
      return (
        <>
          <SubText $onCard>당신은 라이어입니다. 아래 25개 중 정답이 하나 있습니다.</SubText>
          <KeywordGrid>
            {(game.myKeywords || []).map((word) => (
              <KeywordChip key={word} type="button" disabled>
                {word}
              </KeywordChip>
            ))}
          </KeywordGrid>
        </>
      );
    }
    if (game.myKeyword) {
      return (
        <KeywordHero>
          <KeywordHeroLabel>제시어</KeywordHeroLabel>
          <KeywordHeroWord>{game.myKeyword}</KeywordHeroWord>
        </KeywordHero>
      );
    }
    if (!game.myRole) {
      return <SubText $onCard>참가자가 아니면 제시어가 보이지 않습니다. 발언 차례와 결과를 함께 볼 수 있어요.</SubText>;
    }
    return null;
  };
  const renderMafiaSpeakOrder = () => {
    const order = game.speakOrder || [];
    if (!order.length) return null;
    const byRound = {};
    order.forEach((item) => {
      const round = item.round || 1;
      if (!byRound[round]) byRound[round] = [];
      byRound[round].push(item);
    });
    const roundKeys = Object.keys(byRound)
      .map((key) => Number(key))
      .sort((a, b) => a - b);
    return (
      <SpeakOrderBlock>
        <SpeakOrderTitle>발언 순서 · 랜덤 배정 · 각자 {game.speaksPerPlayer || 2}회</SpeakOrderTitle>
        {roundKeys.map((round) => {
          const items = byRound[round] || [];
          return (
            <div key={`round-${round}`}>
              <SpeakOrderRound>{round}라운드</SpeakOrderRound>
              <SpeakOrderList>
                {items.map((item, index) => {
                  const icon = getVacationIcon(item.icon);
                  return (
                    <SpeakOrderItem
                      key={`${item.userId}-${item.index}-${round}`}
                      $current={Boolean(item.current)}
                      $done={Boolean(item.done)}
                    >
                      <SpeakOrderNum>{index + 1}</SpeakOrderNum>
                      <SpeakOrderFace>{icon?.emoji || '🙂'}</SpeakOrderFace>
                      <span>{item.name}</span>
                      <SpeakOrderBadge $current={Boolean(item.current)} $done={Boolean(item.done)}>
                        {item.current ? '지금' : item.done ? '완료' : '대기'}
                      </SpeakOrderBadge>
                    </SpeakOrderItem>
                  );
                })}
              </SpeakOrderList>
            </div>
          );
        })}
      </SpeakOrderBlock>
    );
  };
  const renderMafiaSpeakPanel = () => {
    const speaker = game.currentSpeaker;
    const icon = getVacationIcon(speaker?.icon);
    return (
      <>
        <SpeakBanner $mine={Boolean(speaker?.isMe)}>
          <KeywordHeroLabel>
            발언 {Math.min((game.speaksDone || 0) + 1, game.speaksTotal || 0)} / {game.speaksTotal || 0}
          </KeywordHeroLabel>
          <SpeakName>
            {icon?.emoji || '🎤'} {speaker?.name || '다음 사람'}
          </SpeakName>
          <SubText $onCard style={{ marginTop: '0.35rem' }}>
            {speaker?.isMe
              ? '당신의 차례입니다. 발언이 끝나면 완료를 눌러 주세요.'
              : `${speaker?.name || '참가자'} 님 차례입니다.`}
          </SubText>
        </SpeakBanner>
        {renderMafiaSpeakOrder()}
        {game.canAdvanceSpeak ? (
          <PrimaryButton
            type="button"
            $block
            $mt
            disabled={mafiaSpeakBusy}
            onClick={advanceMafiaSpeak}
          >
            {mafiaSpeakBusy ? '넘기는 중...' : '발언 완료'}
          </PrimaryButton>
        ) : null}
      </>
    );
  };
  const renderMafiaVotePicker = () => (
    <>
      <PickGrid style={{ marginTop: '0.85rem' }}>
        {(game.candidates || game.players || []).map((person) => {
          const selected = selectedMafiaVoteUserId === person.userId;
          const isSelf = person.userId === state?.me?.id;
          const icon = getVacationIcon(person.icon);
          const canPick = Boolean(game.canVote && !loading && !isSelf);
          return (
            <PersonPick
              key={person.userId}
              type="button"
              $on={selected}
              $taken={isSelf}
              disabled={!canPick}
              onClick={canPick ? () => setSelectedMafiaVoteUserId(person.userId) : undefined}
            >
              <PersonFace $on={selected && !isSelf}>
                {icon?.emoji || '🙂'}
              </PersonFace>
              <PersonName>{person.name}</PersonName>
              <PersonState $on={selected && !isSelf}>
                {isSelf ? '나' : selected ? '선택됨' : '선택'}
              </PersonState>
            </PersonPick>
          );
        })}
      </PickGrid>
      <PrimaryButton
        type="button"
        $block
        $mt
        disabled={!selectedMafiaVoteUserId || loading}
        onClick={submitMafiaVote}
      >
        {loading ? '투표 중...' : '투표 완료'}
      </PrimaryButton>
    </>
  );
  const renderMafiaVoteResults = () => {
    const maxVotes = mafiaMaxVotes || 1;
    return (
      <VoteResultList>
        {(game.candidates || []).map((person, index) => {
          const icon = getVacationIcon(person.icon);
          const votes = person.votes || 0;
          const pct = maxVotes > 0 ? Math.round((votes / maxVotes) * 100) : 0;
          return (
            <VoteResultRow key={person.userId} $win={person.accused}>
              <PersonFace $on={person.accused}>
                {icon?.emoji || '🙂'}
              </PersonFace>
              <VotePerson>
                <PersonName>
                  {index + 1}. {person.name}
                </PersonName>
                <PersonState $on={person.accused}>
                  {person.accused ? '지목됨' : person.teamName || ''}
                </PersonState>
                <VoteMeter>
                  <VoteMeterFill $pct={pct} $win={person.accused} />
                </VoteMeter>
              </VotePerson>
              <VoteCount>{votes}표</VoteCount>
            </VoteResultRow>
          );
        })}
      </VoteResultList>
    );
  };
  const renderMafiaGuessPanel = () => (
    <>
      <SubText $onCard>
        라이어가 지목되었습니다. 목록에서 제시어를 맞히면 라이어 승, 틀리면 참가자 승입니다.
      </SubText>
      <KeywordGrid>
        {(game.guessOptions || game.myKeywords || []).map((word) => {
          const selected = selectedMafiaGuess === word;
          return (
            <KeywordChip
              key={word}
              type="button"
              $on={selected}
              $pickable
              disabled={!game.canGuess || loading}
              onClick={() => setSelectedMafiaGuess(word)}
            >
              {word}
            </KeywordChip>
          );
        })}
      </KeywordGrid>
      {game.canGuess ? (
        <PrimaryButton
          type="button"
          $block
          $mt
          disabled={!selectedMafiaGuess || loading}
          onClick={submitMafiaGuess}
        >
          {loading ? '제출 중...' : '제시어 확정'}
        </PrimaryButton>
      ) : (
        <SubText $onCard>라이어가 제시어를 고르는 중입니다.</SubText>
      )}
    </>
  );
  const renderHunminOrder = () => (
    <SpeakOrderBlock>
      <SpeakOrderTitle>발언 순서 · 탈락할 때까지 같은 초성으로 반복</SpeakOrderTitle>
      <SpeakOrderList>
        {(game.turnOrder || []).map((person, index) => {
          const icon = getVacationIcon(person.icon);
          return (
            <SpeakOrderItem
              key={`${person.userId}-${person.index}`}
              $current={Boolean(person.current)}
              $done={Boolean(person.done)}
            >
              <SpeakOrderNum>{index + 1}</SpeakOrderNum>
              <SpeakOrderFace>{icon?.emoji || '🙂'}</SpeakOrderFace>
              <span>{person.name}</span>
              <SpeakOrderBadge $current={Boolean(person.current)} $done={Boolean(person.done)}>
                {person.current ? '지금' : person.done ? '완료' : '대기'}
              </SpeakOrderBadge>
            </SpeakOrderItem>
          );
        })}
      </SpeakOrderList>
    </SpeakOrderBlock>
  );

  const renderHunminGame = ({ showHostControls = false } = {}) => {
    const speaker = game.currentSpeaker;
    const speakerIcon = getVacationIcon(speaker?.icon);
    if (game.status === 'finished') {
      const icon = getVacationIcon(game.winner?.icon);
      return (
        <>
          <KeywordHero>
            <KeywordHeroLabel>최종 1등</KeywordHeroLabel>
            <KeywordHeroWord>{icon?.emoji || '🏆'} {game.winner?.name || '—'}</KeywordHeroWord>
          </KeywordHero>
          <SubText $onCard>훈민정음 최종 우승자입니다.</SubText>
        </>
      );
    }
    return (
      <>
        <KeywordHero>
          <KeywordHeroLabel>{game.round || 1}라운드 · 제시 초성</KeywordHeroLabel>
          <KeywordHeroWord>{game.initials || '—'}</KeywordHeroWord>
        </KeywordHero>
        {renderHunminOrder()}
        <SpeakBanner $mine={Boolean(speaker?.isMe)}>
          <KeywordHeroLabel>현재 발언자</KeywordHeroLabel>
          <SpeakName>{speakerIcon?.emoji || '🎤'} {speaker?.name || '다음 참가자'}</SpeakName>
          <SubText $onCard>
            {speaker?.isMe
              ? '지금 당신의 차례입니다.'
              : `${speaker?.name || '참가자'} 님의 차례입니다.`}
          </SubText>
        </SpeakBanner>
        {hunminFlashWord ? (
          <WordFlash key={hunminFlashWord}>
            <WordFlashLabel>전송됨</WordFlashLabel>
            <WordFlashWord>{hunminFlashWord}</WordFlashWord>
          </WordFlash>
        ) : null}
        {game.canSubmit ? (
          <form onSubmit={submitHunminWord}>
            <Input
              value={hunminWord}
              onChange={(event) => setHunminWord(event.target.value)}
              placeholder={`${game.initials || '초성'}에 맞는 두 글자 단어`}
              autoComplete="off"
              maxLength={12}
            />
            <Row>
              <PrimaryButton type="submit" disabled={loading || !hunminWord.trim()}>
                전송
              </PrimaryButton>
              <GhostButton $onCard type="button" onClick={passHunmin} disabled={loading}>
                포기 · 탈락
              </GhostButton>
            </Row>
          </form>
        ) : null}
        {showHostControls && game.canEliminate ? (
          <>
            <SubText $onCard>관리자 임의 탈락 · 남은 참가자를 누르면 바로 탈락합니다.</SubText>
            <MemberRow>
              {(game.activePlayers || []).map((person) => {
                const icon = getVacationIcon(person.icon);
                return (
                  <MiniButton
                    key={person.userId}
                    type="button"
                    $tone="danger"
                    disabled={loading}
                    onClick={() => eliminateHunminPlayer(person.userId, person.name)}
                  >
                    {icon?.emoji || '·'} {person.name} 탈락
                  </MiniButton>
                );
              })}
            </MemberRow>
          </>
        ) : null}
        {game.isEliminated ? (
          <SubText $onCard>당신은 탈락했습니다. 최종 1위가 나올 때까지 관전만 가능합니다.</SubText>
        ) : null}
        {(game.eliminatedPlayers || []).length ? (
          <>
            <SubText $onCard>탈락자 · 게임 종료까지 재참가 불가</SubText>
            <MemberRow>
              {(game.eliminatedPlayers || []).map((person) => {
                const icon = getVacationIcon(person.icon);
                return (
                  <MemberChip key={`out-${person.userId}`}>
                    {icon?.emoji || '·'} {person.name}
                    <WaitBadge>탈락</WaitBadge>
                  </MemberChip>
                );
              })}
            </MemberRow>
          </>
        ) : null}
        {game.lastEliminated ? (
          <SubText $onCard>
            직전 탈락: {game.lastEliminated.name}
            {game.lastFailureReason ? ` (${game.lastFailureReason})` : ''}
            {' · '}남은 참가자 {game.activePlayers?.length || 0}명
            {' · '}이번 라운드 단어 {game.usedWordCount || 0}개
          </SubText>
        ) : (
          <SubText $onCard>
            이번 라운드 단어 {game.usedWordCount || 0}개 · 같은 라운드에서 이미 쓴 단어는 다시 쓸 수 없습니다.
          </SubText>
        )}
      </>
    );
  };

  const renderWordBag = (teamId, progress, { mine = false } = {}) => {
    const entries = toWordEntries(progress);
    const canVoid = canVoidTeamWords();
    if (!entries.length) return null;
    return (
      <WordBag>
        {entries.map((entry) => {
          const actionable = canVoid && !entry.voided;
          return (
            <WordChip
              key={`${teamId}-${entry.word}`}
              type="button"
              $mine={mine}
              $voided={entry.voided}
              $actionable={actionable}
              disabled={!actionable}
              onClick={actionable ? () => voidChosungWord(teamId, entry.word) : undefined}
              title={
                entry.voided
                  ? `${entry.voidedByName || '검증'} · 무효`
                  : actionable
                    ? '눌러서 무효 처리'
                    : undefined
              }
            >
              {entry.word}
              {entry.voided ? <VoidTag>무효</VoidTag> : null}
            </WordChip>
          );
        })}
      </WordBag>
    );
  };
  const locked = state?.session?.buzzerLocked;
  const buzzerReady = Boolean(state?.session?.buzzerOpen && !locked && myTeam);
  const showHostTools = Boolean(isAdminPage && state?.isHost);
  const buzzerCopy = getBuzzerCopy({
    myTeam,
    locked,
    buzzerReady,
    buzzerOpen: state?.session?.buzzerOpen,
    meId: state?.me?.id,
  });

  const activeHostGameKey = chosungActive
    ? 'chosung'
    : voteActive
      ? 'vote'
      : hunminActive
        ? 'hunmin'
        : mafiaActive
          ? 'liar'
          : drawActive
            ? 'draw'
            : null;
  const hostFocusGame = activeHostGameKey || hostSetupGame;
  const hostGameOption = HOST_GAME_OPTIONS.find((item) => item.id === hostFocusGame) || null;
  const hostWizardStep = activeHostGameKey
    ? 3
    : hostSetupGame
      ? 2
      : 1;

  const toggleRosterSelection = (setter, userId) => {
    setter((prev) => {
      const current = prev == null ? rosterPeople.map((item) => item.userId) : prev;
      return current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
    });
  };

  const renderHostMemberPicker = ({ selectedIds, setter, minCount }) => (
    <SetupSection>
      <SetupSectionTitle>멤버 선택</SetupSectionTitle>
      <Row>
        <MiniButton type="button" onClick={() => setter(rosterPeople.map((person) => person.userId))}>
          모두 선택
        </MiniButton>
        <MiniButton type="button" onClick={() => setter([])}>
          모두 해제
        </MiniButton>
      </Row>
      <PickGrid style={{ marginTop: '0.75rem' }}>
        {rosterPeople.map((person) => {
          const selected = selectedIds.includes(person.userId);
          const icon = getVacationIcon(person.icon);
          return (
            <PersonPick
              key={person.userId}
              type="button"
              $on={selected}
              onClick={() => toggleRosterSelection(setter, person.userId)}
            >
              <PersonFace $on={selected}>{icon?.emoji || '🙂'}</PersonFace>
              <PersonName>{person.name}</PersonName>
              <PersonState $on={selected}>
                {selected ? '참가' : person.teamName || '선택'}
              </PersonState>
            </PersonPick>
          );
        })}
      </PickGrid>
      <SetupHint>
        {selectedIds.length}명 선택
        {selectedIds.length < minCount ? ` · ${minCount}명 이상 필요` : ' · 시작 가능'}
      </SetupHint>
    </SetupSection>
  );

  const renderHostWizardSteps = (step) => (
    <StepRail aria-label="게임 진행 단계">
      {[
        { n: 1, label: '게임 선택' },
        { n: 2, label: hostGameOption?.needsMembers ? '멤버 선택' : '설정' },
        { n: 3, label: activeHostGameKey ? (gameBusy ? '진행 중' : '결과') : '시작' },
      ].map((item) => {
        const done = step > item.n;
        const on = step === item.n;
        return (
          <StepItem key={item.n} $dim={!on && !done}>
            <StepIndex $on={on} $done={done}>{item.n}</StepIndex>
            <StepLabel $on={on} $done={done}>{item.label}</StepLabel>
          </StepItem>
        );
      })}
    </StepRail>
  );

  const renderHostGameRunning = () => {
    if (!hostGameOption) return null;
    return (
      <SetupPanel>
        <SetupTop>
          <div>
            <SetupKicker>Now playing</SetupKicker>
            <SessionHelpHeader
              id={`host-run-${hostGameOption.id}`}
              as="h3"
              title={hostGameOption.title}
              help={hostGameOption.help(game)}
              openId={openHelpId}
              onToggle={toggleHelp}
              compact
            />
          </div>
          {!gameBusy ? (
            <GhostButton
              $onCard
              type="button"
              onClick={() => {
                stopGame();
                setHostSetupGame(null);
              }}
            >
              목록으로
            </GhostButton>
          ) : null}
        </SetupTop>

        {chosungActive ? (
          <>
            <ChosungBoard>
              {Array.from(game.initials || '').map((letter, index) => (
                <ChosungTile key={`${letter}-${index}`}>{letter}</ChosungTile>
              ))}
            </ChosungBoard>
            {game.status === 'reviewing' && pendingTeam ? (
              <SubText $onCard>
                {pendingTeam.name} 팀 단어를 확인하세요. 이상하면 단어를 눌러 무효, 괜찮으면 인정하세요.
              </SubText>
            ) : null}
            {sortedTeams.map((team) => {
              const progress = game.teams?.[String(team.id)] || { count: 0, words: [] };
              return (
                <div key={team.id}>
                  <ProgressNote>
                    {getVacationIcon(team.icon)?.emoji || '○'} {team.name} · {progress.count}/{chosungTargetFor(team)}
                    {game.pendingTeamId === team.id ? ' · 검증 중' : ''}
                  </ProgressNote>
                  {renderWordBag(team.id, progress)}
                </div>
              );
            })}
            {game.status === 'finished' && winnerTeam ? (
              <SubText $onCard>
                {winnerTeam.name} 승리! 확인 후 스코어보드에 +{game.points || 10}점이 들어갔습니다.
              </SubText>
            ) : null}
            <Row>
              {game.status === 'reviewing' ? (
                <>
                  <PrimaryButton type="button" onClick={confirmChosungWin} disabled={loading}>
                    인정 · +{game.points || 10}점
                  </PrimaryButton>
                  <GhostButton $onCard type="button" onClick={stopGame}>게임 종료</GhostButton>
                </>
              ) : game.status === 'running' ? (
                <GhostButton $onCard type="button" onClick={stopGame}>게임 종료</GhostButton>
              ) : (
                <>
                  <PrimaryButton type="button" onClick={startChosungGame} disabled={loading || sortedTeams.length === 0}>
                    한 판 더
                  </PrimaryButton>
                  <GhostButton $onCard type="button" onClick={() => { stopGame(); setHostSetupGame(null); }}>
                    닫기
                  </GhostButton>
                </>
              )}
            </Row>
          </>
        ) : null}

        {voteActive ? (
          <>
            {game.status === 'running' ? (
              <>
                <ProgressNote>
                  {game.votedCount || 0} / {game.totalVoters || 0} 투표
                </ProgressNote>
                <MemberRow>
                  {(game.candidates || []).map((person) => {
                    const icon = getVacationIcon(person.icon);
                    return (
                      <MemberChip key={person.userId}>
                        {icon?.emoji || '·'} {person.name}
                        <WaitBadge $on={person.voted}>
                          {person.voted ? '완료' : '대기'}
                        </WaitBadge>
                      </MemberChip>
                    );
                  })}
                </MemberRow>
                {game.canVote && !myVotePerson ? renderVotePicker() : null}
                {game.canVote && myVotePerson ? renderVoteWaiting() : null}
              </>
            ) : (
              <>
                <SubText $onCard>결과가 공개되었습니다.</SubText>
                {renderVoteResults()}
              </>
            )}
            <Row>
              {game.status === 'running' ? (
                <>
                  <PrimaryButton type="button" onClick={revealVote} disabled={loading}>결과 공개</PrimaryButton>
                  <GhostButton $onCard type="button" onClick={stopGame}>투표 종료</GhostButton>
                </>
              ) : (
                <>
                  <PrimaryButton type="button" onClick={startVoteGame} disabled={loading || rosterCount < 2}>한 판 더</PrimaryButton>
                  <GhostButton $onCard type="button" onClick={() => { stopGame(); setHostSetupGame(null); }}>닫기</GhostButton>
                </>
              )}
            </Row>
          </>
        ) : null}

        {hunminActive ? (
          <>
            {renderHunminGame({ showHostControls: true })}
            <Row>
              {game.status !== 'finished' ? (
                <GhostButton $onCard type="button" onClick={stopGame}>게임 종료</GhostButton>
              ) : (
                <>
                  <PrimaryButton
                    type="button"
                    onClick={() => startHunminGame(selectedHunminIds)}
                    disabled={loading || selectedHunminIds.length < 2}
                  >
                    한 판 더
                  </PrimaryButton>
                  <GhostButton $onCard type="button" onClick={() => { stopGame(); setHostSetupGame(null); }}>닫기</GhostButton>
                </>
              )}
            </Row>
          </>
        ) : null}

        {mafiaActive ? (
          <>
            {game.status === 'speaking' ? renderMafiaSpeakPanel() : null}
            {game.status === 'voting' ? (
              <>
                <ProgressNote>
                  {game.votedCount || 0} / {game.totalVoters || 0} 투표
                </ProgressNote>
                <MemberRow>
                  {(game.candidates || []).map((person) => {
                    const icon = getVacationIcon(person.icon);
                    return (
                      <MemberChip key={person.userId}>
                        {icon?.emoji || '·'} {person.name}
                        <WaitBadge $on={person.voted}>
                          {person.voted ? '완료' : '대기'}
                        </WaitBadge>
                      </MemberChip>
                    );
                  })}
                </MemberRow>
              </>
            ) : null}
            {game.status === 'guessing' ? (
              <>
                {renderMafiaVoteResults()}
                {renderMafiaGuessPanel()}
              </>
            ) : null}
            {game.status === 'finished' ? (
              <>
                {renderMafiaVoteResults()}
                <SubText $onCard>
                  {game.winner === 'liar' || game.winner === 'mafia' ? '라이어 승' : '참가자 승'}
                  {' · '}제시어 {game.keyword}
                  {' · '}라이어 {(game.liar || game.mafia)?.name}
                </SubText>
              </>
            ) : null}
            {['speaking', 'voting', 'guessing'].includes(game.status) ? (
              <SubText $onCard>
                호스트 확인용 · 제시어 {game.hostKeyword || game.keyword}
                {' · '}라이어 {(game.players || []).find((p) => p.userId === (game.hostLiarUserId || game.hostMafiaUserId))?.name || (game.liar || game.mafia)?.name}
              </SubText>
            ) : null}
            <Row>
              {game.status !== 'finished' ? (
                <GhostButton $onCard type="button" onClick={stopGame}>게임 종료</GhostButton>
              ) : (
                <>
                  <PrimaryButton
                    type="button"
                    onClick={() => startMafiaGame(selectedMafiaIds)}
                    disabled={loading || selectedMafiaIds.length < 3}
                  >
                    한 판 더
                  </PrimaryButton>
                  <GhostButton $onCard type="button" onClick={() => { stopGame(); setHostSetupGame(null); }}>닫기</GhostButton>
                </>
              )}
            </Row>
          </>
        ) : null}

        {drawActive ? (
          <>
            <SubText $onCard>
              {game.status === 'running' ? '룰렛이 돌아가는 중이에요.' : '뽑기가 끝났어요. 당첨자를 확인해 주세요.'}
            </SubText>
            <Row>
              {game.status === 'running' ? (
                <GhostButton $onCard type="button" onClick={stopGame}>뽑기 종료</GhostButton>
              ) : (
                <>
                  <PrimaryButton
                    type="button"
                    onClick={() => startDrawGame(selectedDrawIds)}
                    disabled={loading || selectedDrawIds.length < 2}
                  >
                    다시 뽑기
                  </PrimaryButton>
                  <GhostButton $onCard type="button" onClick={() => { stopGame(); setHostSetupGame(null); }}>닫기</GhostButton>
                </>
              )}
            </Row>
          </>
        ) : null}
      </SetupPanel>
    );
  };

  const renderHostGameSetup = () => {
    if (!hostGameOption) return null;
    const option = hostGameOption;
    const selectedCount = option.id === 'hunmin'
      ? selectedHunminIds.length
      : option.id === 'liar'
        ? selectedMafiaIds.length
        : option.id === 'draw'
          ? selectedDrawIds.length
          : rosterCount;
    const canStart = option.needsTeams
      ? sortedTeams.length > 0
      : option.needsMembers
        ? selectedCount >= option.minMembers
        : rosterCount >= Math.max(option.minMembers, 1);

    return (
      <SetupPanel>
        <SetupTop>
          <div>
            <SetupKicker>Setup</SetupKicker>
            <SessionHelpHeader
              id={`host-setup-${option.id}`}
              as="h3"
              title={option.title}
              help={option.help(game)}
              openId={openHelpId}
              onToggle={toggleHelp}
              compact
            />
          </div>
          <GhostButton $onCard type="button" onClick={() => setHostSetupGame(null)}>
            게임 변경
          </GhostButton>
        </SetupTop>

        {option.id === 'chosung' ? (
          <SetupSection>
            <SetupSectionTitle>팀 확인</SetupSectionTitle>
            <SetupHint>
              {sortedTeams.length === 0
                ? '먼저 아래에서 팀을 만든 뒤 시작하세요.'
                : `${sortedTeams.length}개 팀이 준비되어 있습니다.`}
            </SetupHint>
          </SetupSection>
        ) : null}

        {option.id === 'vote' ? (
          <SetupSection>
            <SetupSectionTitle>투표 옵션</SetupSectionTitle>
            <CheckRow>
              <input
                type="checkbox"
                checked={allowSelfVote}
                onChange={(event) => setAllowSelfVote(event.target.checked)}
              />
              <span>나에게 투표하기 허용</span>
            </CheckRow>
            <SetupHint>
              {rosterCount < 2
                ? '팀에 배정된 참가자가 2명 이상이어야 합니다.'
                : `${rosterCount}명이 투표에 참여합니다.`}
            </SetupHint>
          </SetupSection>
        ) : null}

        {option.id === 'hunmin'
          ? renderHostMemberPicker({
            selectedIds: selectedHunminIds,
            setter: setHunminUserIds,
            minCount: 2,
          })
          : null}
        {option.id === 'liar'
          ? renderHostMemberPicker({
            selectedIds: selectedMafiaIds,
            setter: setMafiaUserIds,
            minCount: 3,
          })
          : null}
        {option.id === 'draw'
          ? renderHostMemberPicker({
            selectedIds: selectedDrawIds,
            setter: setDrawUserIds,
            minCount: 2,
          })
          : null}

        <SetupSection>
          <SetupSectionTitle>시작</SetupSectionTitle>
          <Row>
            {option.id === 'chosung' ? (
              <PrimaryButton
                type="button"
                onClick={startChosungGame}
                disabled={loading || !canStart || gameBusy}
              >
                초성 퀴즈 시작
              </PrimaryButton>
            ) : null}
            {option.id === 'vote' ? (
              <PrimaryButton
                type="button"
                onClick={startVoteGame}
                disabled={loading || !canStart || gameBusy}
              >
                투표 시작하기
              </PrimaryButton>
            ) : null}
            {option.id === 'hunmin' ? (
              <PrimaryButton
                type="button"
                onClick={() => startHunminGame(selectedHunminIds)}
                disabled={loading || !canStart || gameBusy}
              >
                훈민정음 시작
              </PrimaryButton>
            ) : null}
            {option.id === 'liar' ? (
              <PrimaryButton
                type="button"
                onClick={() => startMafiaGame(selectedMafiaIds)}
                disabled={loading || !canStart || gameBusy}
              >
                라이어 시작
              </PrimaryButton>
            ) : null}
            {option.id === 'draw' ? (
              <PrimaryButton
                type="button"
                onClick={() => startDrawGame(selectedDrawIds)}
                disabled={loading || !canStart}
              >
                뽑기 시작
              </PrimaryButton>
            ) : null}
          </Row>
          {!canStart ? (
            <SetupHint>
              {option.needsTeams
                ? '팀이 필요합니다.'
                : option.needsMembers
                  ? `참가자를 ${option.minMembers}명 이상 선택해 주세요.`
                  : '참가자가 더 필요합니다.'}
            </SetupHint>
          ) : (
            <SetupHint>준비가 끝났습니다. 시작하면 게스트 화면에 바로 반영됩니다.</SetupHint>
          )}
        </SetupSection>
      </SetupPanel>
    );
  };

  const hasState = Boolean(state);
  useEffect(() => {
    const lockId = locked?.userId || null;
    if (soundReady.current && lockId && lockId !== heardLockId.current) {
      playBuzzerSound();
    }
    heardLockId.current = lockId;
    if (hasState) soundReady.current = true;
  }, [locked?.userId, hasState]);

  return (
    <>
      <PageStyle />
      <Wrapper>
        <Shell>
          {!portalUser ? (
            <>
              <Header>
                <div>
                  <Eyebrow>Seaside resort</Eyebrow>
                  <Title>휴가 레크레이션</Title>
                  <SubText>아이디로 체크인하고, 부저를 선점하세요.</SubText>
                </div>
              </Header>
              <LoginCard>
                {mode === 'login' ? (
                  <form onSubmit={handleLogin}>
                    <Eyebrow $onCard>Check-in</Eyebrow>
                    <Title as="h2" $onCard>리조트 체크인</Title>
                    <Label htmlFor="recreation-login-id">아이디</Label>
                    <Input
                      id="recreation-login-id"
                      value={loginId}
                      onChange={(event) => setLoginId(event.target.value)}
                      placeholder="아이디"
                      autoComplete="username"
                    />
                    <Label htmlFor="recreation-password">비밀번호</Label>
                    <Input
                      id="recreation-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="비밀번호"
                      autoComplete="current-password"
                    />
                    <PrimaryButton type="submit" $block $mt disabled={loading}>
                      {loading ? '체크인 중...' : '체크인'}
                    </PrimaryButton>
                  </form>
                ) : (
                  <form onSubmit={handleSetupPassword}>
                    <Eyebrow $onCard>First visit</Eyebrow>
                    <Title as="h2" $onCard>비밀번호 설정</Title>
                    <Label htmlFor="recreation-new-password">새 비밀번호</Label>
                    <Input
                      id="recreation-new-password"
                      type="password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      autoComplete="new-password"
                    />
                    <Label htmlFor="recreation-confirm-password">비밀번호 확인</Label>
                    <Input
                      id="recreation-confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      autoComplete="new-password"
                    />
                    <PrimaryButton type="submit" $block $mt disabled={loading}>
                      {loading ? '설정 중...' : '입장'}
                    </PrimaryButton>
                  </form>
                )}
                <Message $error={Boolean(message)}>{message}</Message>
              </LoginCard>
            </>
          ) : (
            <>
              <Header>
                <Identity ref={identityRef}>
                  <div>
                    <Eyebrow>{isAdminPage ? 'Host desk' : 'Guest lounge'}</Eyebrow>
                    <Title>{isAdminPage ? '호스트 모드' : '휴가 레크레이션'}</Title>
                    <IdentityMeta>
                      {!isAdminPage && (
                        <FaceWrap>
                          <FaceButton
                            type="button"
                            $small
                            disabled={!myTeam}
                            onClick={() => myTeam && setOpenPicker((prev) => (prev === 'me' ? null : 'me'))}
                            aria-label="내 아이콘"
                          >
                            {getVacationIcon(state?.me?.icon)?.emoji || '🙂'}
                          </FaceButton>
                        </FaceWrap>
                      )}
                      <span>{state?.me?.name || portalUser.login_id}</span>
                      {myTeam ? (
                        <>
                          <span>·</span>
                          {!isAdminPage && (
                            <FaceWrap>
                              <FaceButton
                                type="button"
                                $small
                                onClick={() => setOpenPicker((prev) => (prev === 'team' ? null : 'team'))}
                                aria-label="팀 아이콘"
                              >
                                {getVacationIcon(myTeam.icon)?.emoji || '○'}
                              </FaceButton>
                            </FaceWrap>
                          )}
                          {!isAdminPage ? (
                            <TeamNameButton
                              type="button"
                              onClick={openTeamNameEditor}
                              aria-label="팀 이름 변경"
                            >
                              {myTeam.name}
                            </TeamNameButton>
                          ) : (
                            <span>{myTeam.name}</span>
                          )}
                        </>
                      ) : (
                        <span>· 미배정</span>
                      )}
                      {openPicker === 'me' && renderIconPopover({
                        selectedId: state?.me?.icon,
                        onSelect: setMyIcon,
                      })}
                      {openPicker === 'team' && myTeam && renderIconPopover({
                        selectedId: myTeam.icon,
                        onSelect: (icon) => setTeamIcon(myTeam.id, icon),
                      })}
                      {openPicker === 'teamName' && myTeam && (
                        <NamePopover onSubmit={saveTeamName}>
                          <Label $flush htmlFor="recreation-my-team-name">팀 이름</Label>
                          <Input
                            id="recreation-my-team-name"
                            value={myTeamNameDraft}
                            onChange={(event) => setMyTeamNameDraft(event.target.value)}
                            maxLength={40}
                            autoComplete="off"
                            autoFocus
                          />
                          <PrimaryButton type="submit" $block $mt disabled={loading || !myTeamNameDraft.trim()}>
                            {loading ? '저장 중...' : '저장'}
                          </PrimaryButton>
                        </NamePopover>
                      )}
                    </IdentityMeta>
                  </div>
                </Identity>
                <HeaderActions>
                  {state?.isHost && (
                    <GhostButton
                      type="button"
                      onClick={() => history.push(isAdminPage ? '/portal/recreation' : '/portal/recreation/admin')}
                    >
                      {isAdminPage ? '게스트 화면' : '호스트 데스크'}
                    </GhostButton>
                  )}
                  <GhostButton type="button" onClick={handleLogout}>
                    체크아웃
                  </GhostButton>
                </HeaderActions>
              </Header>

              {isAdminPage && state && !state.isHost && (
                <Card>
                  <Eyebrow $onCard>Restricted</Eyebrow>
                  <Title as="h2" $onCard>호스트 전용</Title>
                  <SubText $onCard>이 페이지는 호스트만 들어갈 수 있습니다.</SubText>
                  <Row>
                    <PrimaryButton type="button" onClick={() => history.push('/portal/recreation')}>
                      게스트 화면으로
                    </PrimaryButton>
                  </Row>
                </Card>
              )}

              {!isAdminPage && voteActive && (
                <Card>
                  <Eyebrow $onCard>Vote</Eyebrow>
                  <SessionHelpHeader
                    id="guest-vote"
                    title={
                      game.status === 'finished'
                        ? '투표 결과'
                        : myVotePerson
                          ? '투표 완료'
                          : !game.canVote
                            ? '기다리는 중'
                            : '한 명에게 투표하세요'
                    }
                    help={`한 사람을 고른 뒤 투표 완료를 눌러 주세요.${game.allowSelfVote ? ' 나에게 투표도 가능합니다.' : ' 나에게는 투표할 수 없습니다.'} 모두 투표하면 결과가 공개됩니다.`}
                    openId={openHelpId}
                    onToggle={toggleHelp}
                  />
                  {game.status === 'running' && game.canVote && !myVotePerson ? (
                    renderVotePicker()
                  ) : null}
                  {game.status === 'running' && (myVotePerson || !game.canVote) ? (
                    renderVoteWaiting()
                  ) : null}
                  {game.status === 'finished' ? (
                    renderVoteResults()
                  ) : null}
                </Card>
              )}

              {!isAdminPage && mafiaActive && (
                <Card>
                  <Eyebrow $onCard>Liar</Eyebrow>
                  <SessionHelpHeader
                    id="guest-liar"
                    title={
                      game.status === 'speaking'
                        ? '라이어 · 발언'
                        : game.status === 'voting'
                          ? '투표하세요!'
                          : game.status === 'guessing'
                            ? '제시어 맞히기'
                            : game.winner === 'liar' || game.winner === 'mafia'
                              ? '라이어 승!'
                              : '참가자 승!'
                    }
                    help="시민에게는 제시어가, 라이어에게는 25개 키워드가 갑니다. 각자 2번씩 발언 후 투표하고, 라이어가 지목되면 제시어를 맞혀야 합니다."
                    openId={openHelpId}
                    onToggle={toggleHelp}
                  />
                  {game.status !== 'finished' ? renderMafiaKeywordPanel() : null}
                  {game.status === 'speaking' ? renderMafiaSpeakPanel() : null}
                  {game.status === 'voting' && game.canVote ? (
                    renderMafiaVotePicker()
                  ) : null}
                  {game.status === 'voting' && !game.canVote ? (
                    <WaitPanel>
                      <WaitFace>{myMafiaVotePerson ? (getVacationIcon(myMafiaVotePerson.icon)?.emoji || '🙂') : '⏳'}</WaitFace>
                      <WaitTitle>기다리는 중</WaitTitle>
                      <WaitCopy>
                        {myMafiaVotePerson
                          ? `${myMafiaVotePerson.name} 님에게 투표했어요. 모두 끝날 때까지 기다려 주세요.`
                          : '참가자만 투표할 수 있어요.'}
                      </WaitCopy>
                      <ProgressNote>
                        {game.votedCount || 0} / {game.totalVoters || 0} 투표
                      </ProgressNote>
                    </WaitPanel>
                  ) : null}
                  {game.status === 'guessing' ? (
                    <>
                      {renderMafiaVoteResults()}
                      {renderMafiaGuessPanel()}
                    </>
                  ) : null}
                  {game.status === 'finished' ? (
                    <>
                      {game.votesRevealed ? renderMafiaVoteResults() : null}
                      <KeywordHero>
                        <KeywordHeroLabel>정답 제시어</KeywordHeroLabel>
                        <KeywordHeroWord>{game.keyword || '—'}</KeywordHeroWord>
                      </KeywordHero>
                      <SubText $onCard>
                        라이어는 {(game.liar || game.mafia)?.name || '알 수 없음'}
                        {game.guess ? ` · 추측: ${game.guess}` : ''}
                        {(game.liarCaught === false || game.mafiaCaught === false)
                          ? ' · 라이어가 지목되지 않아 라이어 승'
                          : game.winner === 'liar' || game.winner === 'mafia'
                            ? ' · 제시어를 맞춰 라이어 승'
                            : ' · 제시어를 틀려 참가자 승'}
                      </SubText>
                    </>
                  ) : null}
                </Card>
              )}

              {!isAdminPage && hunminActive && (
                <Card>
                  <Eyebrow $onCard>Hunminjeongeum</Eyebrow>
                  <SessionHelpHeader
                    id="guest-hunmin"
                    title={game.status === 'finished' ? '훈민정음 종료' : '훈민정음'}
                    help="초성 두 글자에 맞는 단어를 순서대로 전송합니다. 탈락자가 나올 때까지 같은 초성으로 계속 돌아가며, 같은 라운드에서 쓴 단어는 다시 쓸 수 없습니다. 탈락자는 최종 1위가 나오거나 게임이 끝날 때까지 재참가하지 않습니다."
                    openId={openHelpId}
                    onToggle={toggleHelp}
                  />
                  {renderHunminGame()}
                </Card>
              )}

              {!isAdminPage && chosungActive && (
                <Card>
                  <Eyebrow $onCard>Chosung</Eyebrow>
                  <SessionHelpHeader
                    id="guest-chosung"
                    title="초성 퀴즈"
                    help={`이 초성에 맞는 두 글자 단어를 팀 인원 × ${game.wordsPerMember || 2}개 먼저 맞히세요.${myChosungTarget ? ` 우리 팀은 ${myChosungTarget}개입니다.` : ''} 점수는 다른 팀이 확인한 뒤에 들어갑니다.`}
                    openId={openHelpId}
                    onToggle={toggleHelp}
                  />
                  <ChosungBoard>
                    {Array.from(game.initials || '').map((letter, index) => (
                      <ChosungTile key={`${letter}-${index}`}>{letter}</ChosungTile>
                    ))}
                  </ChosungBoard>
                  <ProgressNote>
                    {myGameProgress?.count || 0} / {myChosungTarget || 0}
                  </ProgressNote>
                  {renderWordBag(myTeam?.id, myGameProgress, { mine: true })}
                  {game.status === 'reviewing' && pendingTeam ? (
                    <SubText $onCard>
                      {pendingTeam.id === myTeam?.id
                        ? '다른 팀과 호스트가 단어를 확인하고 있어요. 무효가 되면 다시 맞혀야 합니다.'
                        : `${getVacationIcon(pendingTeam.icon)?.emoji || ''} ${pendingTeam.name} 팀이 ${pendingChosungTarget}개를 맞혔어요. 단어를 확인해 주세요. 이상하면 호스트에게 알려 주세요.`}
                    </SubText>
                  ) : null}
                  {game.status === 'reviewing' && pendingTeam && pendingTeam.id !== myTeam?.id
                    ? renderWordBag(pendingTeam.id, game.teams?.[String(pendingTeam.id)])
                    : null}
                  {game.status === 'running' && myTeam ? (
                    <>
                      {chosungError ? <Message $error>{chosungError}</Message> : null}
                      <AnswerForm onSubmit={submitChosung}>
                        <Input
                          value={chosungWord}
                          onChange={(event) => {
                            setChosungWord(event.target.value);
                            if (chosungError) setChosungError('');
                          }}
                          placeholder="두 글자 단어"
                          autoComplete="off"
                          enterKeyHint="send"
                        />
                        <PrimaryButton type="submit" disabled={loading || !chosungWord.trim()}>
                          {loading ? '확인 중...' : '제출'}
                        </PrimaryButton>
                      </AnswerForm>
                    </>
                  ) : null}
                  {game.status === 'running' && !myTeam ? (
                    <SubText $onCard>팀에 배정되면 참여할 수 있습니다.</SubText>
                  ) : null}
                  {game.status === 'finished' ? (
                    <SubText $onCard>
                      {winnerTeam
                        ? `${getVacationIcon(winnerTeam.icon)?.emoji || ''} ${winnerTeam.name} 팀 단어가 확인되어 +${game.points}점이 들어갔습니다.`
                        : '게임이 끝났습니다.'}
                    </SubText>
                  ) : null}
                </Card>
              )}

              {!isAdminPage && (
                <Card>
                  <Eyebrow $onCard>Buzzer</Eyebrow>
                  <SessionHelpHeader
                    id="guest-buzzer"
                    title="부저"
                    help="호스트가 신호를 열면 부저를 눌러 선점할 수 있습니다. 팀에 배정되어야 사용할 수 있어요."
                    openId={openHelpId}
                    onToggle={toggleHelp}
                  />
                  <BuzzerWrap>
                    <BuzzerButton
                      type="button"
                      $ready={buzzerReady}
                      $pressed={pressed}
                      $locked={Boolean(locked)}
                      disabled={!buzzerReady || loading}
                      onClick={pressBuzzer}
                      aria-label="부저"
                    >
                      <BuzzerText>{buzzerCopy.text}</BuzzerText>
                    </BuzzerButton>
                    <BuzzerPedestal aria-hidden="true" />
                    <BuzzerHint>{buzzerCopy.hint}</BuzzerHint>
                  </BuzzerWrap>
                </Card>
              )}

              <Card>
                <Eyebrow $onCard>Scoreboard</Eyebrow>
                <Title as="h2" $onCard>스코어보드</Title>
                {sortedTeams.length === 0 ? (
                  <SubText $onCard>아직 팀이 없습니다. 호스트가 팀을 만들면 여기에 표시됩니다.</SubText>
                ) : (
                  <ScoreGrid>
                    {sortedTeams.map((team, index) => (
                      <TeamCard
                        key={team.id}
                        $mine={team.id === myTeam?.id}
                        $editing={showHostTools && editingTeamId === team.id}
                      >
                        <TeamName>
                          <TeamIconMark>
                            {getVacationIcon(team.icon)?.emoji || '○'}
                          </TeamIconMark>
                          {team.name}
                          <RankMark>{index === 0 ? 'Lead' : `${index + 1}`}</RankMark>
                        </TeamName>
                        <Score>{team.score}</Score>
                        {team.members.length ? (
                          <MemberRow>
                            {team.members.map((member) => (
                              <MemberChip key={member.userId}>
                                {getVacationIcon(member.icon)?.emoji || '·'} {member.name}
                              </MemberChip>
                            ))}
                          </MemberRow>
                        ) : (
                          <Members>빈 자리</Members>
                        )}
                        {showHostTools && (
                          <>
                            <Row>
                              {[1, 3, 5, 10].map((delta) => (
                                <MiniButton
                                  key={delta}
                                  type="button"
                                  onClick={() => postAction(`/api/recreation/teams/${team.id}/score`, { delta })}
                                >
                                  +{delta}
                                </MiniButton>
                              ))}
                              <MiniButton
                                type="button"
                                onClick={() => postAction(`/api/recreation/teams/${team.id}/score`, { delta: -1 })}
                              >
                                -1
                              </MiniButton>
                            </Row>
                            <Row>
                              <MiniButton type="button" onClick={() => openMemberEditor(team)}>
                                {editingTeamId === team.id ? '멤버 닫기' : '멤버 설정'}
                              </MiniButton>
                              <MiniButton type="button" $tone="danger" onClick={() => deleteTeam(team.id)}>
                                팀 해산
                              </MiniButton>
                            </Row>
                            {editingTeamId === team.id && (
                              <MemberEditor>
                                <PickGrid>
                                  {students.map((student) => {
                                    const selected = selectedUserIds.includes(student.userId);
                                    const occupied = memberTeamByUserId[student.userId];
                                    const taken = Boolean(occupied && occupied.teamId !== team.id);
                                    const icon = getVacationIcon(memberIconByUserId[student.userId] || student.icon);
                                    return (
                                      <PersonPick
                                        key={student.userId}
                                        type="button"
                                        $on={selected}
                                        $taken={taken}
                                        disabled={taken}
                                        onClick={() => {
                                          if (taken) return;
                                          setSelectedUserIds((prev) =>
                                            selected
                                              ? prev.filter((id) => id !== student.userId)
                                              : [...prev, student.userId]
                                          );
                                        }}
                                      >
                                        <PersonFace $on={selected && !taken}>
                                          {icon?.emoji || '🙂'}
                                        </PersonFace>
                                        <PersonName>{student.name}</PersonName>
                                        <PersonState $on={selected && !taken}>
                                          {taken ? occupied.teamName : selected ? '함께' : '선택'}
                                        </PersonState>
                                      </PersonPick>
                                    );
                                  })}
                                </PickGrid>
                                <Row>
                                  <PrimaryButton type="button" onClick={saveMembers}>
                                    저장
                                  </PrimaryButton>
                                  <GhostButton $onCard type="button" onClick={() => setEditingTeamId(null)}>
                                    닫기
                                  </GhostButton>
                                </Row>
                              </MemberEditor>
                            )}
                          </>
                        )}
                      </TeamCard>
                    ))}
                  </ScoreGrid>
                )}
              </Card>

              {showHostTools && (
                <>
                  <Card>
                    <Eyebrow $onCard>Games</Eyebrow>
                    <Title as="h2" $onCard>게임</Title>
                    {renderHostWizardSteps(hostWizardStep)}

                    {activeHostGameKey ? (
                      renderHostGameRunning()
                    ) : !hostSetupGame ? (
                      <GameCatalog>
                        {HOST_GAME_OPTIONS.map((option) => (
                          <GameTile
                            key={option.id}
                            type="button"
                            onClick={() => setHostSetupGame(option.id)}
                          >
                            <GameTileMark>{option.mark}</GameTileMark>
                            <GameTileCopy>
                              <GameTileTitle>{option.title}</GameTileTitle>
                              <GameTileMeta>{option.meta}</GameTileMeta>
                            </GameTileCopy>
                          </GameTile>
                        ))}
                      </GameCatalog>
                    ) : (
                      renderHostGameSetup()
                    )}
                  </Card>

                  <Card>
                    <Eyebrow $onCard>Round</Eyebrow>
                    <SessionHelpHeader
                      id="host-buzzer"
                      title="라운드 신호"
                      help="신호를 열면 게스트가 부저를 누를 수 있습니다."
                      openId={openHelpId}
                      onToggle={toggleHelp}
                    />
                    <Row>
                      <PrimaryButton type="button" onClick={() => postAction('/api/recreation/buzzer/open')}>
                        신호 열기
                      </PrimaryButton>
                      <GhostButton $onCard type="button" onClick={() => postAction('/api/recreation/buzzer/reset')}>
                        다시 대기
                      </GhostButton>
                    </Row>
                  </Card>

                  <Card>
                    <Eyebrow $onCard>Teams</Eyebrow>
                    <SessionHelpHeader
                      id="host-teams"
                      title="팀 만들기"
                      help="아이콘은 팀원들이 직접 고릅니다."
                      openId={openHelpId}
                      onToggle={toggleHelp}
                    />
                    <form onSubmit={createTeam}>
                      <Label htmlFor="recreation-team-name">팀 이름</Label>
                      <Input
                        id="recreation-team-name"
                        value={teamName}
                        onChange={(event) => setTeamName(event.target.value)}
                        placeholder="예: 선셋, 테라스"
                      />
                      <PrimaryButton type="submit" $mt disabled={loading || !teamName.trim()}>
                        팀 추가
                      </PrimaryButton>
                    </form>
                  </Card>

                  <Card>
                    <DangerButton type="button" onClick={resetGame}>
                      전체 초기화
                    </DangerButton>
                  </Card>
                </>
              )}

              <Message $error={Boolean(message)}>{message}</Message>
            </>
          )}
        </Shell>
        {drawActive && (
          <DrawOverlay>
            <DrawStage>
              <DrawEyebrow>Lucky draw</DrawEyebrow>
              <DrawTitle>
                {game.status === 'finished' ? '당첨!' : '누가 뽑힐까요?'}
              </DrawTitle>
              <DrawRoulette game={game} />
              {state?.isHost && (
                <Row style={{ justifyContent: 'center', marginTop: '1rem' }}>
                  {game.status === 'finished' ? (
                    <>
                      <PrimaryButton
                        type="button"
                        onClick={() => startDrawGame(selectedDrawIds)}
                        disabled={loading || selectedDrawIds.length < 2}
                      >
                        다시 뽑기
                      </PrimaryButton>
                      <GhostButton type="button" onClick={stopGame}>
                        닫기
                      </GhostButton>
                    </>
                  ) : (
                    <GhostButton type="button" onClick={stopGame}>
                      뽑기 종료
                    </GhostButton>
                  )}
                </Row>
              )}
            </DrawStage>
          </DrawOverlay>
        )}
        {locked && !drawActive && (
          <LockOverlay>
            <LockFlash />
            <LockCard>
              <LockStamp>BUZZ</LockStamp>
              <LockFace>
                {getVacationIcon(locked.icon || locked.teamIcon)?.emoji || '⚡'}
              </LockFace>
              <LockName>{locked.name}</LockName>
              <LockTeam>
                {getVacationIcon(locked.teamIcon)?.emoji || ''}
                {locked.teamName ? ` ${locked.teamName}` : ''} 선점
              </LockTeam>
              {state?.isHost && (
                <Row style={{ justifyContent: 'center' }}>
                  <PrimaryButton type="button" onClick={() => postAction('/api/recreation/buzzer/reset')}>
                    다시 대기
                  </PrimaryButton>
                </Row>
              )}
            </LockCard>
          </LockOverlay>
        )}
      </Wrapper>
    </>
  );
};

export default RecreationPage;
